import React, { useRef, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut, Pie } from 'react-chartjs-2';
import './FindingsWidgets.css';
import './FindingsWidgets_Landscape.css';
import { getLabel, getLabelStyle } from './widgetLabelHelpers';
import { getFindingIcon, getFindingSvgIconComponent } from './findingsConstants';

// Register Chart.js components for pie chart
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Helper to determine the best contrast color (Black or White) for a given background
 * @param {string} hexcolor - Hex color string
 * @returns {string} '#000000' or '#ffffff'
 */
const getContrastColor = (hexcolor) => {
  if (!hexcolor || hexcolor === 'transparent' || hexcolor === 'none') return '#374151';
  
  // Strip # if present
  let color = hexcolor;
  if (color.charAt(0) === '#') {
    color = color.substring(1);
  }

  // Handle shorthand hex like #ABC
  if (color.length === 3) {
    color = color.split('').map(char => char + char).join('');
  }

  // Convert to RGB
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Get YIQ ratio (standard formula for luminance)
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  
  // Return black for light backgrounds, white for dark backgrounds
  return (yiq >= 128) ? '#000000' : '#ffffff';
};

/**
 * Extract findings data from JSON scan results
 * This mimics how template1.js and template2.js extract findings from exportdata
 *
 * Expected structure from backend:
 * exportdata = {
 *   "Category Name": [
 *     [{ outerText, innerListItems, color, score }, ...],  // findings rectangles
 *     "95"  // overall score
 *   ]
 * }
 */

/**
 * Finding Item Component - Shared by multiple widgets
 */
const FindingItem = ({ 
  finding, 
  style = {}, 
  config = {}, 
  styleTemplate = 'classic',
  indicatorGroup = {}, 
  findingOverrides = {},
  iconSize = 100,
  className = ""
}) => {
  const showIcon = config.showIcon !== false;
  const showLabel = config.showLabel !== false;
  const showLabelBg = config.showLabelBg !== false;

  // Resolve styles for this specific item (global + override)
  const { indicatorIcon, indicatorLabel, indicatorLabelBg, indicatorBackground, indicatorBorder } = indicatorGroup;
  const overrides = findingOverrides[finding.name] || {};
  const itemBgColor = overrides.sfItemBgColor || style.sfItemBgColor || "transparent";
  const itemLabelBgColor = overrides.sfLabelBgColor || style.sfLabelBgColor || "transparent";
  
  // Determine effective text color based on background vs indicator target
  let itemTextColor = overrides.sfTextColor || style.sfTextColor;
  
  if (!itemTextColor) {
    if (indicatorLabelBg) {
      // Background is the indicator color, so text color must contrast with it
      itemTextColor = getContrastColor(finding.bgColor);
    } else if (indicatorBackground) {
      // Whole item background is the indicator color
      itemTextColor = getContrastColor(finding.bgColor);
    } else if (indicatorLabel) {
      // Text color itself is the indicator
      itemTextColor = finding.bgColor;
    } else {
      // Default fallback color
      itemTextColor = "#374151";
    }
  }
  const itemIconColor = overrides.sfIconColor || style.sfIconColor || null;
  const itemFontSize = overrides.sfTextFontSize || style.sfTextFontSize || 14;
  const itemFontWeight = overrides.sfTextFontWeight || style.sfTextFontWeight || '700';
  const itemTextStyle = overrides.sfTextStyle || style.sfTextStyle || 'normal';
  const itemTextDecoration = overrides.sfTextDecoration || style.sfTextDecoration || 'none';
  const itemTextAlign = overrides.sfTextTextAlign || style.sfTextTextAlign || 'center';
  const itemFlexDirection = overrides.sfItemFlexDirection || style.sfItemFlexDirection || 'column';
  const itemTextWrap = overrides.sfTextWrap || style.sfTextWrap || 'wrap';
  
  const itemBorderWidth = overrides.sfItemBorderWidth !== undefined ? overrides.sfItemBorderWidth : (style.sfItemBorderWidth !== undefined ? style.sfItemBorderWidth : 0);
  const itemBorderStyle = overrides.sfItemBorderStyle || style.sfItemBorderStyle || "none";
  const itemBorderColor = overrides.sfItemBorderColor || style.sfItemBorderColor || "#e5e7eb";
  const effectiveBorderColor = indicatorBorder ? finding.bgColor : itemBorderColor;
  const itemBorder = itemBorderStyle !== "none" ? `${itemBorderWidth}px ${itemBorderStyle} ${effectiveBorderColor}` : "none";
  const itemBorderRadius = overrides.sfItemBorderRadius !== undefined ? overrides.sfItemBorderRadius : (style.sfItemBorderRadius !== undefined ? style.sfItemBorderRadius : null);

  const IconComponent = getFindingSvgIconComponent(finding.name, finding.configuredIcon);

  if (styleTemplate === 'modern') {
    const barRadius = overrides.sfModernBarRadius !== undefined ? overrides.sfModernBarRadius : (style.sfModernBarRadius !== undefined ? style.sfModernBarRadius : 3);
    const barWidth = overrides.sfModernBarWidth !== undefined ? overrides.sfModernBarWidth : (style.sfModernBarWidth !== undefined ? style.sfModernBarWidth : '100%');
    const barHeight = overrides.sfModernBarHeight !== undefined ? overrides.sfModernBarHeight : (style.sfModernBarHeight !== undefined ? style.sfModernBarHeight : 4);

    return (
      <div 
        className={`${className}-modern`}
        style={{ 
            backgroundColor: itemBgColor,
            border: itemBorder,
            borderRadius: itemBorderRadius !== null ? `${itemBorderRadius}px` : '4px'
        }}
      >
        <div className={`${className}-icon-modern`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <IconComponent 
            color={itemIconColor || finding.bgColor}
            size="100%"
            strokeWidth={1.5}
            style={{ maxWidth: `${iconSize}%`, maxHeight: `${iconSize}%` }} 
          />
        </div>
        
        <div
          className={`${className}-label-modern`}
          style={{
            color: itemTextColor,
            fontSize: `${itemFontSize}px`,
            fontWeight: itemFontWeight,
            fontStyle: itemTextStyle,
            textDecoration: itemTextDecoration,
            textAlign: itemTextAlign,
            backgroundColor: showLabelBg ? (indicatorLabelBg ? finding.bgColor : (itemLabelBgColor || 'transparent')) : 'transparent',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          {overrides.customLabel || finding.name}
        </div>

        <div 
          className={`${className}-bar-modern`}
          style={{ 
            backgroundColor: finding.bgColor,
            borderRadius: `${barRadius}px`,
            width: typeof barWidth === 'number' ? `${barWidth}px` : barWidth,
            height: `${barHeight}px`,
            margin: '0 auto',
            marginTop: '2px'
          }}
        />
      </div>
    );
  }

  // Flexible / Classic
  const isFlexible = styleTemplate === 'flexible';
  const itemStyle = {
    backgroundColor: indicatorBackground ? finding.bgColor : itemBgColor,
    border: itemBorder,
    borderRadius: itemBorderRadius !== null ? `${itemBorderRadius}px` : ((indicatorBackground || indicatorBorder) ? (isFlexible ? '12px' : '8px') : (isFlexible ? '8px' : '0')),
    padding: (indicatorBackground || indicatorBorder) ? (isFlexible ? '12px' : '10px') : (itemBorder !== 'none' ? (isFlexible ? '12px' : '10px') : '0'),
    display: 'flex',
    flexDirection: itemFlexDirection,
    alignItems: 'center',
    justifyContent: 'center',
    gap: isFlexible ? '12px' : '8px',
    width: '100%',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    backgroundColor: showLabelBg ? (indicatorLabelBg ? finding.bgColor : (itemLabelBgColor || 'transparent')) : 'transparent',
    color: itemTextColor,
    fontSize: `${itemFontSize}px`,
    fontWeight: itemFontWeight,
    fontStyle: itemTextStyle,
    textDecoration: itemTextDecoration,
    textAlign: itemTextAlign,
    borderRadius: '4px',
    padding: indicatorLabel ? '6px 8px' : '2px 4px',
    whiteSpace: itemTextWrap === 'nowrap' ? 'nowrap' : 'normal',
    width: '100%',
    boxSizing: 'border-box',
    flex: itemFlexDirection.startsWith('row') ? '1' : 'initial'
  };

  const iconColor = itemIconColor || (indicatorIcon ? finding.bgColor : (itemTextColor || '#374151'));

  return (
    <div className={`${className} ${isFlexible ? 'template-flexible-item' : ''}`} style={itemStyle}>
      {showIcon && (
        <div className={`${className}-icon`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <IconComponent 
            color={iconColor} 
            size={isFlexible ? "100%" : 64} 
            strokeWidth={1.5}
            style={{ maxWidth: isFlexible ? `${iconSize}%` : '100%', maxHeight: isFlexible ? `${iconSize}%` : '100%' }} 
          />
        </div>
      )}
      {showLabel && (
        <div
          className={`${className}-label`}
          style={labelStyle}
        >
          {overrides.customLabel || finding.name}
        </div>
      )}
    </div>
  );
};

/**
 * Findings Status Widget - Shows security findings grid with icons and colored labels
 * Matches the design from New_template.js and New_template_Docx.js createScoreCard functions
 */
export const FindingsStatusWidget = ({ data, config = {}, style = {}, theme = {}, orientation = 'portrait' }) => {
  const getImageBaseUrl = () => '/s3/images';
  const imageBaseUrl = getImageBaseUrl();

  // Extract findings from data - format: [name, status, scoreStr, message, cost]
  const findings = Array.isArray(data)
    ? data
    : (data?.findings || data?.scanData?.findings || []);

  // Process findings data similar to backend
  const processedFindings = findings.map((finding) => {
    if (!Array.isArray(finding) || finding.length < 3) return null;

    const name = finding[0] || '';
    const status = finding[1] || '';
    const scoreStr = finding[2];

    // Parse score
    let score = null;
    if (scoreStr !== null && scoreStr !== undefined) {
      const scoreNum = typeof scoreStr === 'string'
        ? parseFloat(scoreStr.replace('%', ''))
        : parseFloat(scoreStr);
      score = isNaN(scoreNum) ? null : scoreNum;
    }

    const ACCEPTABLE = data?.acceptable_score_threshold ?? data?.scanData?.acceptable_score_threshold ?? 20;
    const sColors = {
      green: theme?.statusColors?.green || '#40B657',
      yellow: theme?.statusColors?.yellow || '#FCC419',
      red: theme?.statusColors?.red || '#FF0000'
    };

    // Get dynamic status using category thresholds
    const calculatedStatus = getFindingStatus(name, score, data.category_thresholds, ACCEPTABLE);
    
    let bgColor = '#808080'; // gray
    if (score !== null) {
      if (calculatedStatus === 'Critical' || status === 'Critical') {
        bgColor = sColors.red;
      } else if (calculatedStatus === 'Warning' || status === 'At Risk' || status === 'Warning') {
        bgColor = sColors.yellow;
      } else {
        bgColor = sColors.green;
      }
    }

    // Get image path using shared helper (For PDF/DOCX)
    const imagePath = getFindingIcon(name, score, style?.findingOverrides?.[name], imageBaseUrl);

    // Resolve configurable SVG icon
    let configuredIcon = null;
    if (data?.category_thresholds) {
       // Support both flat format (category as key) and grouped format (scan type as key)
       let matchedCat = data.category_thresholds[name] || data.category_thresholds[style?.findingOverrides?.[name]?.customLabel || name];
       
       if (!matchedCat) {
          for (const key of Object.keys(data.category_thresholds)) {
             const value = data.category_thresholds[key];
             if (Array.isArray(value)) {
                matchedCat = value.find(c => c.name === name || c.name === (style?.findingOverrides?.[name]?.customLabel || name));
                if (matchedCat) break;
             }
          }
       }

       if (matchedCat) {
         configuredIcon = (matchedCat.mode === 'custom' && matchedCat.custom_thresholds?.icon) 
           ? matchedCat.custom_thresholds.icon 
           : matchedCat.icon;
       }
    }

    return {
      name,
      score,
      imagePath,
      configuredIcon,
      bgColor,
      status,
      message: finding[3] || '',
      cost: finding[4] || 0
    };
  }).filter(f => f !== null);

  // Header Title Style
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#344054',
    textAlign: 'left'
  }, 'findings-status');

  // Handle empty findings
  if (processedFindings.length === 0) {
    const noDataStyle = getLabelStyle(config, 'noDataMessage', {}, 'findings-status');

    return (
      <div className="findings-status-widget-scorecard" style={style}>
        {/* Title - matches backend "Report of Findings For:" */}
        <div className="findings-status-title">
          <h2 style={titleStyle}>Report of Findings for:</h2>
        </div>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#6b7280',
          fontSize: '14px',
          ...noDataStyle
        }}>
          No findings data available
        </div>
      </div>
    );
  }

  // Get configuration
  const styleTemplate = config.styleTemplate || 'classic';
  const layout = config.layout || 'grid';
  const iconSize = config.iconSize || 100;
  
  // Indicators
  const indicatorTarget = config.indicatorTarget || 'both';
  const indicatorIcon = styleTemplate === 'flexible' ? !!config.indicatorIcon : (styleTemplate === 'classic');
  const indicatorLabel = styleTemplate === 'flexible' ? !!config.indicatorLabel : (styleTemplate === 'classic' && (indicatorTarget === 'text' || indicatorTarget === 'both'));
  const indicatorLabelBg = styleTemplate === 'flexible' ? !!config.indicatorLabelBg : (styleTemplate === 'classic' && (indicatorTarget === 'label' || indicatorTarget === 'both' || indicatorTarget === 'Label Bg'));
  const indicatorBackground = styleTemplate === 'flexible' ? !!config.indicatorBackground : false;
  const indicatorBorder = styleTemplate === 'flexible' ? !!config.indicatorBorder : false;

  const findingOverrides = style.findingOverrides || {};

  // Build container style based on layout mode
  const isLandscape = orientation === 'landscape';
  let columns = config.gridColumns || (isLandscape ? 5 : 4);
  if (!config.gridColumns && processedFindings.length <= 2) columns = 2;
  if (!config.gridColumns && processedFindings.length > 2 && processedFindings.length <= 5) columns = 3;
  const effectiveColumns = Math.min(columns, processedFindings.length);

  const containerStyle = layout === 'grid'
    ? {
        display: 'grid',
        gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
        gap: '12px',
        width: '100%'
      }
    : {
        display: 'flex',
        flexDirection: layout === 'column' ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: '12px',
        width: '100%',
        justifyContent: (style.sfTextTextAlign === 'center' || style.sfTextTextAlign === 'right') ? (style.sfTextTextAlign === 'center' ? 'center' : 'flex-end') : 'flex-start'
      };

  return (
    <div className={`findings-status-widget-scorecard ${styleTemplate === 'flexible' ? 'template-flexible' : ''} ${styleTemplate === 'modern' ? 'template-modern' : ''}`} style={style}>
      {/* Title - matches backend "Report of Findings For:" */}
      <div className="findings-status-title">
        <h2 style={titleStyle}>Report of Findings for:</h2>
      </div>

      {/* Grid/Flex container of findings */}
      <div
        className="findings-status-container"
        style={containerStyle}
      >
        {processedFindings.map((finding, idx) => (
          <FindingItem 
            key={idx}
            finding={finding}
            style={style}
            config={config}
            styleTemplate={styleTemplate}
            indicatorGroup={{ indicatorIcon, indicatorLabel, indicatorLabelBg, indicatorBackground, indicatorBorder }}
            findingOverrides={findingOverrides}
            iconSize={iconSize}
            className="findings-status-item"
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Single Finding Status Widget
 * Displays one or more finding status items with icon and colored label.
 * Users add/remove findings in the Content panel and control layout, indicator target, and styles.
 */
export const SingleFindingStatusWidget = ({ 
  data, 
  config = {}, 
  style = {}, 
  theme = {}, 
  onConfigChange, 
  scanGroups = [], 
  availableCategories = [],
  allPossibleFindings
}) => {
  const getImageBaseUrl = () => '/s3/images';
  const imageBaseUrl = getImageBaseUrl();

  // Extract findings from data using robust helper (fallback to local if not provided)
  const rawFindings = allPossibleFindings || recreateFindingsArray(data);

  // Process all findings
  const processedFindings = rawFindings.map((finding) => {
    if (!Array.isArray(finding) || finding.length < 3) return null;
    const name = finding[0] || '';
    const status = finding[1] || '';
    const scoreStr = finding[2];
    let score = null;
    if (scoreStr !== null && scoreStr !== undefined) {
      const scoreNum = typeof scoreStr === 'string'
        ? parseFloat(scoreStr.replace('%', ''))
        : parseFloat(scoreStr);
      score = isNaN(scoreNum) ? null : scoreNum;
    }
    const sColors = {
      green: theme?.statusColors?.green || '#40B657',
      yellow: theme?.statusColors?.yellow || '#FCC419',
      red: theme?.statusColors?.red || '#FF0000'
    };
    const ACCEPTABLE = data?.acceptable_score_threshold ?? data?.scanData?.acceptable_score_threshold ?? 20;
    
    // Get dynamic status using category thresholds
    const calculatedStatus = getFindingStatus(name, score, data.category_thresholds, ACCEPTABLE);
    
    const effectiveStatus = calculatedStatus || status;
    let bgColor = '#808080';

    if (effectiveStatus === 'Critical' || effectiveStatus === 'Red' || effectiveStatus === 'Bad') {
      bgColor = sColors.red;
    } else if (effectiveStatus === 'Warning' || effectiveStatus === 'Yellow' || effectiveStatus === 'At Risk' || effectiveStatus === 'Moderate') {
      bgColor = sColors.yellow;
    } else if (effectiveStatus === 'Healthy' || effectiveStatus === 'Green' || effectiveStatus === 'Good' || effectiveStatus === 'Pass') {
      bgColor = sColors.green;
    } else if (score !== null) {
      if (score < 20) bgColor = sColors.green;
      else if (score < 50) bgColor = sColors.yellow;
      else bgColor = sColors.red;
    }
    // Get image path using shared helper (for PDF/Docx modes)
    const imagePath = getFindingIcon(name, score, style?.findingOverrides?.[name], imageBaseUrl);
    
    // Resolve configurable SVG icon
    let configuredIcon = null;
    if (data?.category_thresholds) {
       // Support both flat format (category as key) and grouped format (scan type as key)
       let matchedCat = data.category_thresholds[name] || data.category_thresholds[style?.findingOverrides?.[name]?.customLabel || name];
       
       if (!matchedCat) {
          for (const key of Object.keys(data.category_thresholds)) {
             const value = data.category_thresholds[key];
             if (Array.isArray(value)) {
                matchedCat = value.find(c => c.name === name || c.name === (style?.findingOverrides?.[name]?.customLabel || name));
                if (matchedCat) break;
             }
          }
       }
 
       if (matchedCat) {
         configuredIcon = (matchedCat.mode === 'custom' && matchedCat.custom_thresholds?.icon) 
           ? matchedCat.custom_thresholds.icon 
           : matchedCat.icon;
       }
    }

    return { name, score, imagePath, configuredIcon, bgColor, status: calculatedStatus || status, message: finding[3] || '', cost: finding[4] || 0 };
  }).filter(f => f !== null);

  // Backward compat: config.selectedFinding (string) → wrap into array
  let selectedNames = config.selectedFindings;
  if (!selectedNames || !Array.isArray(selectedNames)) {
    if (config.selectedFinding) {
      selectedNames = [config.selectedFinding];
    } else {
      selectedNames = processedFindings.length > 0 ? [processedFindings[0].name] : [];
    }
  }

  // Resolve selected findings to their processed data
  const activeFindings = selectedNames
    .map(name => {
      const found = processedFindings.find(f => f.name === name);
      if (found) return found;

      // If not found in processed data, create a placeholder so it still renders
      const imagePath = getFindingIcon(name, null, style?.findingOverrides?.[name], imageBaseUrl);
      return { 
        name, 
        score: null, 
        imagePath, 
        configuredIcon: null, 
        bgColor: '#808080', 
        status: 'N/A', 
        message: '', 
        cost: 0 
      };
    })
    .filter(Boolean);

  // Config
  const styleTemplate = config.styleTemplate || 'classic'; // 'classic' | 'modern'
  // Both templates now respect config.layout (defaulting to row if not set)
  const layout = config.layout || 'row';
  const iconSize = config.iconSize || 100;
  // Indicator target is only relevant for Classic template
  const indicatorTarget = config.indicatorTarget || 'both';

  // New Flexible Template Configs
  const showIcon = config.showIcon !== false;
  const showLabel = config.showLabel !== false;
  const indicatorIcon = styleTemplate === 'flexible' ? !!config.indicatorIcon : (styleTemplate === 'classic'); // Icon is indicator in classic
  const indicatorLabel = styleTemplate === 'flexible' ? !!config.indicatorLabel : (styleTemplate === 'classic' && (indicatorTarget === 'text' || indicatorTarget === 'both'));
  const indicatorLabelBg = styleTemplate === 'flexible' ? !!config.indicatorLabelBg : (styleTemplate === 'classic' && (indicatorTarget === 'label' || indicatorTarget === 'both' || indicatorTarget === 'Label Bg'));
  const indicatorBackground = styleTemplate === 'flexible' ? !!config.indicatorBackground : false;
  const indicatorBorder = styleTemplate === 'flexible' ? !!config.indicatorBorder : false;

  // Target-specific overrides logic
  const findingOverrides = style.findingOverrides || {};

  // Default values for basic props
  const defaultFontSize = 14;
  const defaultFontWeight = '700';
  const defaultTextStyle = 'normal';
  const defaultTextDecoration = 'none';
  const defaultTextAlign = 'left';

  // Handle no findings
  if (activeFindings.length === 0) {
    return (
      <div className="single-finding-status-widget" style={style}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', flex: 1, color: '#6b7280', fontSize: '14px',
          textAlign: 'center', padding: '10px'
        }}>
          No findings selected
        </div>
      </div>
    );
  }

  // Grid columns should be exactly what the user configured, 
  // UNLESS they have fewer active findings than the configured amount (to prevent empty space).
  const configuredColumns = config.gridColumns || 3;
  const effectiveColumns = Math.min(configuredColumns, activeFindings.length);

  // Build container style based on layout mode
  const containerStyle = layout === 'grid'
    ? {
        display: 'grid',
        gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
        gap: '12px',
        width: '100%'
      }
    : {
        display: 'flex',
        flexDirection: layout === 'column' ? 'column' : 'row',
        flexWrap: 'wrap', // Ensure rows can wrap if they get too small
        gap: '12px',
        width: '100%'
      };  return (
    <div className={`single-finding-status-widget ${styleTemplate === 'modern' ? 'template-modern' : ''}`} style={style}>
      <div className="single-finding-status-container" style={containerStyle}>
        {activeFindings.map((finding, idx) => (
          <FindingItem 
            key={idx}
            finding={finding}
            style={style}
            config={config}
            styleTemplate={styleTemplate}
            indicatorGroup={{ indicatorIcon, indicatorLabel, indicatorLabelBg, indicatorBackground, indicatorBorder }}
            findingOverrides={findingOverrides}
            iconSize={iconSize}
            className="single-finding-status-item"
          />
        ))}
      </div>
    </div>
  );
};
;

/**
 * Mapping of Finding Categories to Scan Types
 * This helps group findings by their source scan (Localhost, Network, AD, etc.)
 */
export const SCAN_TYPE_MAPPING = {
  // Localhost Scan
  'System Information': 'Localhost Scan',
  'Hardware': 'Localhost Scan',
  'Installed Applications': 'Localhost Scan',
  'Local Services': 'Localhost Scan',
  'Local Firewall': 'Localhost Scan',
  'OS Version': 'Localhost Scan',
  'Antivirus (Localhost Scan)': 'Localhost Scan',
  'Firewall (Localhost Scan)': 'Localhost Scan',
  'OS (Localhost Scan)': 'Localhost Scan',
  'Services (Localhost Scan)': 'Localhost Scan',
  'Open Ports (Localhost Scan)': 'Localhost Scan',
  'Softwares': 'Localhost Scan',
  'Drive': 'Localhost Scan',
  'Age': 'Localhost Scan',
  'Encryption': 'Localhost Scan',
  'Localhost Scan': 'Localhost Scan',
  
  // Network Scan
  'Backups': 'Network Scan',
  'Network Services': 'Network Scan',
  'Open Ports': 'Network Scan',
  'Network Firewall': 'Network Scan',
  'Antivirus (Network Scan)': 'Network Scan',
  'Encryption (Network Scan)': 'Network Scan',
  'OS (Network Scan)': 'Network Scan',
  'Services (Network Scan)': 'Network Scan',
  'Open Ports (Network Scan)': 'Network Scan',
  'Age (Network Scan)': 'Network Scan',
  'Drive (Network Scan)': 'Network Scan',
  'Softwares (Network Scan)': 'Network Scan',
  'Vulnerabilities': 'Network Scan',
  'Sensitive Open Ports': 'Network Scan',
  'Network Scan': 'Network Scan',
  
  // Active Directory Scan
  'AD Users': 'AD Scan',
  'AD Computers': 'AD Scan',
  'AD Groups': 'AD Scan',
  'AD OUs': 'AD Scan',
  'FSMO Roles': 'AD Scan',
  'GPO Analysis': 'AD Scan',
  'Password Policy': 'AD Scan',
  'Domain Controllers': 'AD Scan',
  'Accounts': 'AD Scan',
  'OU': 'AD Scan',
  'Admins': 'AD Scan',
  'Policy': 'AD Scan',
  'FSMO': 'AD Scan',
  'GPO': 'AD Scan',
  'Active Directory Scan': 'AD Scan',
  
  // MS365 Scan
  'MS Users': 'MS365 Scan',
  'MFA': 'MS365 Scan',
  'MS Policy': 'MS365 Scan',
  'MS365 Users': 'MS365 Scan',
  'MFA Status': 'MS365 Scan',
  'MS365 Groups': 'MS365 Scan',
  'SharePoint': 'MS365 Scan',
  'OneDrive': 'MS365 Scan',
  'Teams': 'MS365 Scan',
  'Azure AD': 'MS365 Scan',
  'Secure Score': 'MS365 Scan',
  'MS365 Policies': 'MS365 Scan',

  // Dark Web Scan
  'Dark Web': 'Dark Web Scan',

  // PII Scan
  'Personally Identifiable Information': 'PII Scan',
  'PII Detection': 'PII Scan',
  'Data Privacy': 'PII Scan',
  'PII': 'PII Scan'
};

// Canonical list of categories that should always be present (from Screenshot and Requests)
export const MANDATORY_CATEGORIES = [
  // Localhost Scan
  'Antivirus', 'OS (Localhost Scan)', 'Services (Localhost Scan)', 'Hardware', 'Softwares', 'Drive', 'Age',
  // MS365 Scan
  'MS Users', 'MFA', 'MS Policy',
  // PII Scan
  'PII',
  // AD Scan
  'Admins', 'AD Computers', 'AD Users', 'OU', 'AD Groups',
  // Network Scan
  'Backups', 'Network Services', 'Open Ports (Network Scan)', 'Firewall (Network Scan)', 'OS (Network Scan)', 'Services (Network Scan)', 'Encryption'
];



/**
 * Helper to check if a category matches a scan type
 * Matches the logic in KeyFindingsSummaryWidget filter
 */
export function matchesScanType(finding, scanType) {
  // 1. Check explicit search result from recreateFindingsArray
  if (finding?.[6] === scanType) return true;
  if (finding?.category === scanType || finding?.group === scanType || finding?.scan_type === scanType) return true;
  
  // 2. Legacy fallback to name-based dictionary
  let categoryName = '';
  if (Array.isArray(finding)) {
    categoryName = finding[0];
  } else if (typeof finding === 'object' && finding !== null) {
    categoryName = finding.title || finding.name || '';
  } else if (typeof finding === 'string') {
    categoryName = finding;
  }

  if (!scanType || scanType === 'All') return true;
  if (!categoryName || typeof categoryName !== 'string') return false;

  // Exact match check
  if (categoryName === scanType) return true;

  // 1. Check for explicit suffix in category name (e.g., "Firewall (Localhost Scan)")
  if (categoryName.includes(`(${scanType})`)) {
    return true;
  }

  // 2. Check for mapped scan type
  const mappedScanType = SCAN_TYPE_MAPPING[categoryName];
  if (mappedScanType === scanType) {
    return true;
  }

  // 3. Substring match fallback (e.g., "AD Groups" should match "AD Scan" if scanType is "AD Scan")
  if (scanType === 'AD Scan' && categoryName.startsWith('AD ')) return true;
  if (scanType === 'MS365 Scan' && (categoryName.startsWith('MS365 ') || categoryName.startsWith('MS '))) return true;

  // 4. Dynamic match for custom scan types
  if (!['Localhost Scan', 'Network Scan', 'AD Scan', 'MS365 Scan', 'PII Scan'].includes(scanType)) {
      if (categoryName.toLowerCase().includes(scanType.toLowerCase())) return true;
      if (scanType.toLowerCase().includes(categoryName.toLowerCase())) return true;
  }

  return false;
}

/**
 * Key Findings Summary Widget
 * Shows compliance percentages and status for each category
 * Widget auto-sizes to fit all content
 * Supports filtering by scanType (Localhost Scan, Network Scan, etc.)
 */
export const KeyFindingsSummaryWidget = ({ data, config = {}, style = {}, pagination, theme = {} }) => {
  // console.log('[KeyFindingsSummaryWidget] props data:', data);

  const {
    title = 'Key Findings Summary',
    sortField,
    sortDirection = 'asc',
    scanType = 'All', // New filter property
    hideNoData = false // Filter to remove "No Data" categories
  } = { ...config };

  // Sync handles both Content tab (showX) and Data tab (hiddenColumns)
  const hiddenCols = config?.hiddenColumns || [];
  const showCategory = (config.showCategory !== false) && !hiddenCols.includes('Category');
  const showStatus = (config.showStatus !== false) && !hiddenCols.includes('Status');
  const showCompliance = (config.showCompliance !== false) && !hiddenCols.includes('Compliance');
  const showNotes = (config.showNotes !== false) && !hiddenCols.includes('Notes');

  // Custom column header names
  const customColumnHeaders = config?.customColumnHeaders || {};
  const getDisplayHeader = (name) => customColumnHeaders[name] || name;

  const tColors = theme?.statusColors || {};
  const statusColors = {
    'green': style.auditColorGreen || tColors.green || '#16a34a',
    'yellow': style.auditColorYellow || tColors.yellow || '#eab308',
    'red': style.auditColorRed || tColors.red || '#dc2626',
    'gray': style.auditColorGray || tColors.gray || '#9ca3af',
  };

  // Extract all summaries
  let allSummaries = extractKeyFindingsSummary(data, config.customThresholdNotes);

  // Apply scanType filtering if not "All"
  if (scanType && scanType !== 'All') {
    allSummaries = allSummaries.filter(summary => {
      // 1. Check for explicit suffix in category name (e.g., "Firewall (Localhost Scan)")
      if (summary.category.includes(`(${scanType})`)) {
        return true;
      }

      // 2. Check for mapped scan type
      const mappedScanType = SCAN_TYPE_MAPPING[summary.category];
      if (mappedScanType === scanType) {
        return true;
      }

      // 3. Substring match fallback (e.g., "AD Groups" should match "AD Scan" if scanType is "AD Scan")
      // Only do this for scans that have short common prefixes
      if (scanType === 'AD Scan' && summary.category.startsWith('AD ')) return true;
      if (scanType === 'MS365 Scan' && summary.category.startsWith('MS365 ')) return true;

      // DEBUG: Log unmapped categories to help troubleshoot empty filters
      // (Only if it's the right "type" of thing but missing from map)
      if (!mappedScanType && summary.category) {
        // console.warn(`[KeyFindingsSummary] Unmapped category observed: "${summary.category}". This item will be hidden when filtered.`);
      }

      return false;
    });
  }

  // Filter out "No Data" categories if hideNoData is enabled
  if (hideNoData) {
    allSummaries = allSummaries.filter(summary => 
      summary.statusColor !== 'gray'
    );
  }



  // Apply sorting if configured
  if (sortField && sortDirection) {
    allSummaries = [...allSummaries].sort((a, b) => {
      let aValue, bValue;

      if (sortField === 'category') {
        aValue = a.category || '';
        bValue = b.category || '';
      } else if (sortField === 'status') {
        aValue = a.status || '';
        bValue = b.status || '';
      } else if (sortField === 'compliance') {
        aValue = a.compliance || 0;
        bValue = b.compliance || 0;
      } else {
        return 0;
      }

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? -1 : 1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  // Apply pagination if provided
  // Use startIndex if available (for different itemsPerPage on first vs continuation pages), otherwise calculate it
  const summaries = pagination
    ? (() => {
      const startIndex = pagination.startIndex !== undefined
        ? pagination.startIndex
        : pagination.currentPage * pagination.itemsPerPage;
      const endIndex = startIndex + pagination.itemsPerPage;
      return allSummaries.slice(startIndex, endIndex);
    })()
    : allSummaries;

  // Dynamic grid columns based on visible columns
  const colSizes = [];
  if (showCategory) colSizes.push('2fr');
  if (showStatus) colSizes.push('1fr');
  if (showCompliance) colSizes.push('1.5fr');
  if (showNotes) colSizes.push('3fr');
  const gridColumns = colSizes.length > 0 ? colSizes.join(' ') : '1fr';

  console.log('[KeyFindingsSummaryWidget] Rendering with summaries:', summaries.length, summaries);

  // Extract style values with fallbacks
  const widgetTextColor = style?.textColor || undefined;
  const widgetBgColor = style?.backgroundColor || undefined;
  const widgetTitleColor = style?.textColor || undefined;

  const widgetTitleStyle = getLabelStyle(config, 'title', {}, 'key-findings');
  const headerStyle = getLabelStyle(config, 'columnHeaders', {}, 'key-findings');
  const dataRowStyle = getLabelStyle(config, 'dataRows', {}, 'key-findings');

  return (
    <div
      className="key-findings-summary-widget"
      style={{
        ...style,
        backgroundColor: widgetBgColor,
        color: widgetTextColor
      }}
    >
      {title && (
        <h3
          className="findings-widget-title"
          style={{ ...widgetTitleStyle, color: widgetTitleStyle.color || widgetTitleColor }}
        >
          {title}
        </h3>
      )}

      {summaries.length === 0 ? (
        <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: widgetTextColor || '#666' }}>
          <p>No findings available for this scan data.</p>
          <p style={{ fontSize: '12px', marginTop: '10px' }}>
            Check settings or verify the uploaded scan file.
          </p>
        </div>
      ) : (
        <div className="key-findings-table">
          <div
            className="key-findings-header"
            style={{
              gridTemplateColumns: gridColumns,
              ...headerStyle,
              color: headerStyle.color || widgetTextColor
            }}
          >
            {showCategory && <div style={headerStyle}>{getDisplayHeader('Category')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader('Status')}</div>}
            {showCompliance && <div style={headerStyle}>{getDisplayHeader('Compliance')}</div>}
            {showNotes && <div style={headerStyle}>{getDisplayHeader('Notes')}</div>}
          </div>
          {summaries.map((summary, idx) => {
            return (
              <div
                key={idx}
                className="key-finding-row"
                style={{
                  gridTemplateColumns: gridColumns,
                  ...dataRowStyle,
                  color: dataRowStyle.color || widgetTextColor
                }}
              >
                {showCategory && <div className="finding-category" style={{ ...dataRowStyle, color: dataRowStyle.color || widgetTextColor }}>{summary.category}</div>}
                {showStatus && (
                  <div
                    className={`finding-status status-${summary.statusColor}`}
                    style={statusColors[summary.statusColor] ? {
                      ...dataRowStyle,
                      backgroundColor: statusColors[summary.statusColor],
                      color: getContrastColor(statusColors[summary.statusColor])
                    } : { ...dataRowStyle, color: widgetTextColor || dataRowStyle.color }}
                  >
                    {summary.status}
                  </div>
                )}
                {showCompliance && (
                  <div className="finding-compliance">
                    <div className="compliance-bar">
                      <div
                        className={`compliance-fill status-${summary.statusColor}`}
                        style={{ 
                          width: `${summary.compliance}%`,
                          ...(statusColors[summary.statusColor] ? { backgroundColor: statusColors[summary.statusColor] } : {})
                        }}
                      ></div>
                    </div>
                    <span style={{ ...dataRowStyle, color: dataRowStyle.color || widgetTextColor }}>{summary.compliance}%</span>
                  </div>
                )}
                {showNotes && (
                  <div className="finding-notes" style={{ ...dataRowStyle, color: dataRowStyle.color || widgetTextColor }}>{summary.notes}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Open Ports Widget - Shows list of open ports grouped by category
 * Widget auto-sizes to fit all content
 */
export const OpenPortsWidget = ({ data, config = {}, style = {}, pagination }) => {
  const {
    title = 'Sensitive Open Ports',
    sortField,
    sortDirection = 'asc'
  } = config;
  const widgetType = 'open-ports';

  // Sync handles both Content tab (showX) and Data tab (hiddenColumns)
  const hiddenCols = config?.hiddenColumns || [];
  const showIP = (config.showIP !== false) && !hiddenCols.includes('IP Address');
  const showPort = (config.showPort !== false) && !hiddenCols.includes('Port');
  const showService = (config.showService !== false) && !hiddenCols.includes('Service');

  // Get customizable labels
  const columnHeaders = getLabel(config, widgetType, 'columnHeaders');
  const noDataMessage = getLabel(config, widgetType, 'noDataMessage');

  // Get label styles
  const titleStyle = getLabelStyle(config, 'title', {}, widgetType);
  const headerStyle = getLabelStyle(config, 'columnHeaders', {}, widgetType);
  const dataRowStyle = getLabelStyle(config, 'dataRows', {}, widgetType);
  const noDataStyle = getLabelStyle(config, 'noDataMessage', {}, widgetType);

  // Extract all ports
  const allPortsData = extractOpenPortsData(data);
  let portsToDisplay = allPortsData.all;

  // Apply sorting if configured
  if (sortField && sortDirection) {
    portsToDisplay = [...portsToDisplay].sort((a, b) => {
      let aValue, bValue;

      if (sortField === 'ip') {
        aValue = a.ip || a.device || '';
        bValue = b.ip || b.device || '';
      } else if (sortField === 'port') {
        aValue = a.port || 0;
        bValue = b.port || 0;
      } else if (sortField === 'service') {
        aValue = a.service || '';
        bValue = b.service || '';
      } else {
        return 0;
      }

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? -1 : 1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  // Apply pagination if provided
  // Use startIndex if available (for different itemsPerPage on first vs continuation pages), otherwise calculate it
  const displayPorts = pagination
    ? (() => {
      const startIndex = pagination.startIndex !== undefined
        ? pagination.startIndex
        : pagination.currentPage * pagination.itemsPerPage;
      const endIndex = startIndex + pagination.itemsPerPage;
      return portsToDisplay.slice(startIndex, endIndex);
    })()
    : portsToDisplay;

  // Dynamic grid columns based on visible columns
  // const gridColumns = [showIP, showPort, showService].filter(Boolean).length;
  let templateColumns = '';
  if (showIP && showPort && showService) {
    templateColumns = '2fr 1fr 2fr'; // IP, Port, Service
  } else if (showIP && showPort) {
    templateColumns = '2fr 1fr'; // IP, Port
  } else if (showIP && showService) {
    templateColumns = '2fr 2fr'; // IP, Service
  } else if (showPort && showService) {
    templateColumns = '1fr 2fr'; // Port, Service
  } else {
    templateColumns = '1fr'; // Single column
  }

  // Extract style values with fallbacks
  const widgetTextColor = style?.textColor || undefined;
  const widgetBgColor = style?.backgroundColor || undefined;

  return (
    <div
      className="open-ports-widget"
      style={{
        ...style,
        backgroundColor: widgetBgColor,
        color: widgetTextColor
      }}
    >
      {title && (
        <h3
          className="findings-widget-title"
          style={{ ...titleStyle, color: widgetTextColor || titleStyle.color }}
        >
          {title}
        </h3>
      )}

      {displayPorts.length === 0 ? (
        <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: widgetTextColor || '#666', ...noDataStyle }}>
          <p>{noDataMessage}</p>
        </div>
      ) : (
        <div className="open-ports-table">
          {/* Header Row */}
          <div
            className="open-ports-header"
            style={{
              gridTemplateColumns: templateColumns,
              color: widgetTextColor
            }}
          >
            {showIP && <div style={headerStyle}>{columnHeaders[0]}</div>}
            {showPort && <div style={headerStyle}>{columnHeaders[1]}</div>}
            {showService && <div style={headerStyle}>{columnHeaders[2]}</div>}
          </div>

          {/* Data Rows */}
          <div className="open-ports-body">
            {displayPorts.map((port, idx) => (
              <div
                key={idx}
                className="open-ports-row"
                style={{
                  gridTemplateColumns: templateColumns,
                  color: widgetTextColor
                }}
              >
                {showIP && <div className="port-ip" style={{ ...dataRowStyle, color: widgetTextColor || dataRowStyle.color }}>{port.ip || port.device}</div>}
                {showPort && <div className="port-number" style={{ ...dataRowStyle, color: widgetTextColor || dataRowStyle.color }}>{port.port}</div>}
                {showService && <div className="port-service" style={{ ...dataRowStyle, color: widgetTextColor || dataRowStyle.color }}>{port.service || 'Unknown'}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Findings Grid Widget - Visual grid showing all findings with color coding
 * Similar to template1.js nested rectangles
 * Widget auto-sizes to fit all content
 */
export const FindingsGridWidget = ({ data, config = {}, style = {}, pagination, theme = {} }) => {
  const {
    title = 'Findings Overview',
    category = null, // null = all categories, or specify one
    showScores = true,
    columns = 3,
    sortField,
    sortDirection = 'asc'
  } = config;

  const tColors = theme?.statusColors || {};
  const statusColors = {
    'green': style.auditColorGreen || tColors.green || '#16a34a',
    'yellow': style.auditColorYellow || tColors.yellow || '#eab308',
    'red': style.auditColorRed || tColors.red || '#dc2626',
    'gray': '#9ca3af'
  };

  const widgetType = 'findings-grid';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  let allFindings = extractAllFindings(data, category);

  // Apply sorting if configured
  if (sortField && sortDirection) {
    allFindings = [...allFindings].sort((a, b) => {
      let aValue, bValue;

      if (sortField === 'title') {
        aValue = a.title || '';
        bValue = b.title || '';
      } else if (sortField === 'score') {
        aValue = a.score || 0;
        bValue = b.score || 0;
      } else if (sortField === 'color') {
        // Sort by color: red < yellow < green < gray
        const colorOrder = { red: 0, yellow: 1, green: 2, gray: 3 };
        aValue = colorOrder[a.color] !== undefined ? colorOrder[a.color] : 99;
        bValue = colorOrder[b.color] !== undefined ? colorOrder[b.color] : 99;
      } else {
        return 0;
      }

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? -1 : 1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  // Apply pagination if provided
  // Use startIndex if available (for different itemsPerPage on first vs continuation pages), otherwise calculate it
  const findings = pagination
    ? (() => {
      const startIndex = pagination.startIndex !== undefined
        ? pagination.startIndex
        : pagination.currentPage * pagination.itemsPerPage;
      const endIndex = startIndex + pagination.itemsPerPage;
      return allFindings.slice(startIndex, endIndex);
    })()
    : allFindings;

  return (
    <div className="findings-grid-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="findings-cards" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {findings.map((finding, idx) => (
          <div key={idx} className={`finding-card-detailed status-${finding.color}`}
            style={statusColors[finding.color] ? { borderTopColor: statusColors[finding.color] } : {}}
          >
            <div className="finding-header">
              <h4 style={statusColors[finding.color] ? { color: statusColors[finding.color] } : {}}>{finding.title}</h4>
              {showScores && finding.score && (
                <span className={`finding-score score-${finding.color}`}
                  style={statusColors[finding.color] ? { color: statusColors[finding.color], borderColor: statusColors[finding.color], backgroundColor: `${statusColors[finding.color]}15` } : {}}
                >{finding.score}%</span>
              )}
            </div>
            <div className="finding-body">
              <ul>
                {finding.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={`finding-footer status-${finding.color}`}
              style={statusColors[finding.color] ? { 
                backgroundColor: statusColors[finding.color],
                color: getContrastColor(statusColors[finding.color])
              } : {}}
            >
              {finding.color === 'red' && 'Requires Attention'}
              {finding.color === 'yellow' && 'Needs Improvement'}
              {finding.color === 'green' && 'Satisfactory'}
              {finding.color === 'gray' && 'No Findings'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Open Ports Findings Summary Widget
 * Shows Total Devices, Affected Devices, Percentage
 */
export const OpenPortsFindingsSummaryWidget = ({ data, config = {}, style = {} }) => {
  const { title = 'Open Ports Findings Summary' } = config;
  const summary = extractOpenPortsSummary(data);

  const widgetType = 'open-ports-summary';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyle = getLabelStyle(config, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const valueStyle = getLabelStyle(config, 'values', {
    fontSize: '14px',
    fontWeight: '600',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  return (
    <div className="findings-summary-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="summary-table">
        <div className="summary-row header">
          <div style={headerStyle}>Total Devices</div>
          <div style={headerStyle}>Affected Devices</div>
          <div style={headerStyle}>Percentage</div>
        </div>
        <div className="summary-row">
          <div className="summary-value" style={valueStyle}>{summary.totalDevices}</div>
          <div className="summary-value affected" style={valueStyle}>{summary.affectedDevices}</div>
          <div className="summary-value percentage" style={valueStyle}>{summary.percentage}%</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Dark Web Scanning Summary Widget
 * Shows Total Emails, Breached Emails, Percentage
 */
export const DarkWebSummaryWidget = ({ data, config = {}, style = {} }) => {
  const { title = 'Dark Web Scanning Summary' } = config;
  const summary = extractDarkWebSummary(data);

  const widgetType = 'darkweb-summary';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyle = getLabelStyle(config, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const valueStyle = getLabelStyle(config, 'values', {
    fontSize: '14px',
    fontWeight: '600',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  return (
    <div className="findings-summary-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="summary-table">
        <div className="summary-row header">
          <div style={headerStyle}>Total Emails</div>
          <div style={headerStyle}>Breached Emails</div>
          <div style={headerStyle}>Percentage</div>
        </div>
        <div className="summary-row">
          <div className="summary-value" style={valueStyle}>{summary.totalEmails}</div>
          <div className="summary-value affected" style={valueStyle}>{summary.breachedEmails}</div>
          <div className="summary-value percentage" style={valueStyle}>{summary.percentage}%</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Accounts Finding Summary Widget (Type 1 - Password)
 * Shows Total Accounts, Non-Expiring Passwords, Percentage
 */
export const AccountsFindingsSummaryWidget = ({ data, config = {}, style = {} }) => {
  const { title = 'Accounts Finding Summary', type = 'password' } = config;
  const summary = type === 'admin' ? extractAdminsSummary(data) : extractAccountsSummary(data);

  const widgetType = type === 'admin' ? 'accounts-admin-summary' : 'accounts-summary';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyle = getLabelStyle(config, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyle = getLabelStyle(config, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  if (type === 'admin') {
    // Type 2: Admins breakdown
    return (
      <div className="findings-summary-widget" style={style}>
        {title && <h3 className="findings-widget-title" style={titleStyle}>{title} - Admins</h3>}
        <div className="summary-table">
          <div className="summary-row admins-row header">
            <div style={headerStyle}>Total Admins</div>
            <div style={headerStyle}>Category</div>
            <div style={headerStyle}>Total</div>
            <div style={headerStyle}>Cumulative</div>
          </div>
          {summary.admins.map((admin, idx) => (
            <div key={idx} className="summary-row admins-row">
              {idx === 0 ? (
                <>
                  <div className="summary-value" style={rowStyle}>{summary.totalAdmins}</div>
                  <div className="summary-value" style={rowStyle}>{admin.category}</div>
                  <div className="summary-value" style={rowStyle}>{admin.total}</div>
                  <div className="summary-value" style={rowStyle}>{admin.cumulative}</div>
                </>
              ) : (
                <>
                  <div className="summary-value" style={rowStyle}></div>
                  <div className="summary-value" style={rowStyle}>{admin.category}</div>
                  <div className="summary-value" style={rowStyle}>{admin.total}</div>
                  <div className="summary-value" style={rowStyle}>{admin.cumulative}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Type 1: Password expiration
  return (
    <div className="findings-summary-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="summary-table">
        <div className="summary-row header">
          <div style={headerStyle}>Total Accounts</div>
          <div style={headerStyle}>Non-Expiring Password</div>
          <div style={headerStyle}>Percentage</div>
        </div>
        <div className="summary-row">
          <div className="summary-value" style={rowStyle}>{summary.totalAccounts}</div>
          <div className="summary-value affected" style={rowStyle}>{summary.nonExpiring}</div>
          <div className="summary-value percentage" style={rowStyle}>{summary.percentage}%</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Extract Localhost Firewall Status Data
 * Matches backend logic from template2/Localhost.js:621-659
 */
const extractLocalhostFirewallStatus = (data) => {
  try {
    // Extract from raw scan data - match backend _Localhost_Firewall_status logic
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};

    const rows = [];
    // let notFullyEnabledCount = 0;
    const name = 'localhost';

    // Backend: const firewall = localhost?.["System"]?.Firewall || localhost?.Firewall;
    const firewall = localhost?.["System"]?.Firewall || localhost?.Firewall;

    if (!firewall || typeof firewall !== 'object') {
      // Backend: rows.push([name, '-', 'Disabled']);
      rows.push({
        device: name,
        profiles: '-',
        status: 'Disabled'
      });
      // notFullyEnabledCount++;
    } else {
      // Backend: const domain = firewall.Domain === true; const privateNet = firewall.Private === true; const publicNet = firewall.Public === true;
      const domain = firewall.Domain === true;
      const privateNet = firewall.Private === true;
      const publicNet = firewall.Public === true;

      // Backend: const profileSummary = `Domain: ${domain ? 'Enabled' : 'Disabled'}, Private: ${privateNet ? 'Enabled' : 'Disabled'}, Public: ${publicNet ? 'Enabled' : 'Disabled'}`;
      const profileSummary = `Domain: ${domain ? 'Enabled' : 'Disabled'}, Private: ${privateNet ? 'Enabled' : 'Disabled'}, Public: ${publicNet ? 'Enabled' : 'Disabled'}`;

      // Backend: const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;
      const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;

      // Backend: let status = 'Disabled'; if (enabledCount === 3) { status = 'Fully Enabled'; } else if (enabledCount > 0) { status = 'Partially Enabled'; notFullyEnabledCount++; } else { notFullyEnabledCount++; }
      let status = 'Disabled';
      if (enabledCount === 3) {
        status = 'Fully Enabled';
      } else if (enabledCount > 0) {
        status = 'Partially Enabled';
        // notFullyEnabledCount++;
      } else {
        // notFullyEnabledCount++;
      }

      // Backend: rows.push([name, profileSummary, status]);
      rows.push({
        device: name,
        profiles: profileSummary,
        status: status
      });
    }

    return rows;
  } catch (error) {
    console.log('Error extractLocalhostFirewallStatus', error);
    return [];
  }
};

/**
 * Firewall Findings Summary Widget
 * Displays Localhost Firewall Status table matching backend template2/Localhost.js:531-543
 */
export const FirewallFindingsSummaryWidget = ({ data, config = {}, style = {} }) => {
  const {
    title = 'Localhost Firewall Status'
  } = config;

  const widgetType = config.widgetType || 'firewall-summary';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyle = getLabelStyle(config, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyle = getLabelStyle(config, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract localhost firewall status data
  const firewallRows = extractLocalhostFirewallStatus(data);

  // Backend headers: ['Device', 'Firewall Profiles', 'Status']
  // Grid columns: 3 columns (Device, Firewall Profiles, Status)
  const templateColumns = '1fr 2fr 1fr';

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      {firewallRows.length === 0 ? (
        <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          <p>No firewall data available.</p>
        </div>
      ) : (
        <div className="open-ports-table">
          {/* Header Row */}
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns }}>
            <div style={headerStyle}>Device</div>
            <div style={headerStyle}>Firewall Profiles</div>
            <div style={headerStyle}>Status</div>
          </div>

          {/* Data Rows */}
          <div className="open-ports-body">
            {firewallRows.map((row, idx) => (
              <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns }}>
                <div className="port-service" style={rowStyle}>{row.device}</div>
                <div className="port-service" style={rowStyle}>{row.profiles}</div>
                <div className="port-service" style={rowStyle}>
                  <span style={{
                    color: row.status === 'Fully Enabled' ? '#22c55e' : row.status === 'Partially Enabled' ? '#f59e0b' : '#ef4444',
                    fontWeight: 'bold'
                  }}>
                    {row.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * Service Risk Summary Widget
 * Shows Total Devices, Affected Devices, Percentage
 */
export const ServiceRiskSummaryWidget = ({ data, config = {}, style = {} }) => {
  const { title = 'Service Risk Summary' } = config;
  const summary = extractServiceRiskSummary(data);

  const widgetType = config.widgetType || 'service-risk-summary';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyle = getLabelStyle(config, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const valueStyle = getLabelStyle(config, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  return (
    <div className="findings-summary-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="summary-table">
        <div className="summary-row header">
          <div style={headerStyle}>Total Devices</div>
          <div style={headerStyle}>Affected Devices</div>
          <div style={headerStyle}>Percentage</div>
        </div>
        <div className="summary-row">
          <div className="summary-value" style={valueStyle}>{summary.totalDevices}</div>
          <div className="summary-value affected" style={valueStyle}>{summary.affectedDevices}</div>
          <div className="summary-value percentage" style={valueStyle}>{summary.percentage}%</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Overall Risk Score Widget - MATCHES GENERATED REPORT EXACTLY
 * Replicates finalPageDOCX() from New_template_Docx.js lines 250-463
 */
export const OverallRiskScoreWidget = ({ data, config = {}, style = {}, isExporting = false, orientation = 'portrait', theme = {} }) => {
  const width = parseInt(style?.width) || 0;
  const height = parseInt(style?.height) || 0;
  const isSmall = (width > 0 && width < 400) || (height > 0 && height < 500);

  const { 
    title = 'Overall Risk Core',
    showRatingKey = !isSmall,
    showRiskStatus = !isSmall,
    showBusinessProtection = !isSmall,
    showChart = true,
    chartType = 'doughnut'
  } = config;
  const riskData = extractOverallRiskScore(data);
  const tColors = theme?.statusColors || {};
  const chartRef = useRef(null);
  
  const isLandscape = orientation === 'landscape';

  const widgetType = config.widgetType || 'overall-risk-score';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  
  // Prepare chart data


  const chartData = {
    labels: ['High Risks', 'Medium Risks'],
    datasets: [{
      data: [riskData.highRiskCount, riskData.lowRiskCount],
      backgroundColor: [tColors.red || style.auditColorRed || '#ef4444', tColors.yellow || style.auditColorYellow || '#f59e0b'],
      borderColor: '#ffffff',
      borderWidth: 2,
      cutout: chartType === 'pie' ? 0 : '70%'
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Hide legend, we'll show it separately
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            return `${label}: ${value}`;
          }
        }
      }
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    }
  };

  // Force chart resize on export
  useEffect(() => {
    if (isExporting && chartRef.current) {
      setTimeout(() => {
        const canvas = chartRef.current?.querySelector('canvas');
        if (canvas) {
          const chartInstance = ChartJS.getChart(canvas);
          if (chartInstance) {
            chartInstance.resize();
          }
        }
      }, 100);
    }
  }, [isExporting]);

  return (
    <div className={`overall-risk-widget ${isLandscape ? 'landscape-mode' : ''} ${isSmall ? 'small-mode' : ''} ${!showChart ? 'no-chart-mode' : ''} ${!showRatingKey ? 'no-key-mode' : ''}`} style={style}>
      {/* Title */}
      {title && <h2 className="overall-risk-title" style={titleStyle}>{title}</h2>}

      {/* Rating Key Header */}
      {showRatingKey && (
        <>
          <h3 className="rating-key-header">Rating Key</h3>
          <div className="rating-key-table">
            {/* Headers */}
            <div className="rating-header-cell">Good</div>
            <div className="rating-header-cell">Low Risk</div>
            <div className="rating-header-cell">High Risk</div>

            {/* Values */}
            <div className="rating-value-cell rating-good" style={{ backgroundColor: tColors.green || style.auditColorGreen || undefined }}>
              <div className="rating-range">0% - {data?.acceptable_score_threshold ?? 20}%</div>
              <div className="rating-label">ACCEPTABLE</div>
            </div>
            <div className="rating-value-cell rating-moderate" style={{ backgroundColor: tColors.yellow || style.auditColorYellow || undefined }}>
              <div className="rating-range">{ (data?.acceptable_score_threshold ?? 20) + 1 }% - 50%</div>
              <div className="rating-label">AT RISK</div>
            </div>
            <div className="rating-value-cell rating-high" style={{ backgroundColor: tColors.red || style.auditColorRed || undefined }}>
              <div className="rating-range">51% - 100%</div>
              <div className="rating-label">CRITICAL RISK</div>
            </div>
          </div>
        </>
      )}

      {/* Chart Section */}
      {showChart && (
        <div className="risk-pie-chart-container" data-export-ignore="false">
          {riskData.highRiskCount === 0 && riskData.lowRiskCount === 0 ? (
            <div className="no-risks-message">✓ No High or Medium Risks Found</div>
          ) : (
            <div className="risk-pie-chart-wrapper" style={{ 
              flexDirection: config.legendPosition === 'left' ? 'row-reverse' : 
                             config.legendPosition === 'right' ? 'row' : 
                             config.legendPosition === 'bottom' ? 'column' : 'column-reverse',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: config.legendPosition === 'top' ? '20px' : '0',
              gap: (config.legendPosition === 'left' || config.legendPosition === 'right') ? '40px' : '24px'
            }}>
              <div className="risk-pie-chart-canvas-container" ref={chartRef}>
                {chartType === 'pie' ? (
                  <Pie
                    data={chartData}
                    options={chartOptions}
                    style={{ position: 'relative' }}
                  />
                ) : (
                  <Doughnut
                    data={chartData}
                    options={chartOptions}
                    style={{ position: 'relative' }}
                  />
                )}

              </div>
              {/* Legend */}
              <div className="pie-chart-legend" style={{ 
                display: config.legendPosition === 'none' ? 'none' : 'flex',
                flexDirection: (config.legendPosition === 'left' || config.legendPosition === 'right') ? 'column' : 'row',
                marginTop: (config.legendPosition === 'left' || config.legendPosition === 'right' || config.legendPosition === 'top') ? '0' : '20px',
                alignItems: (config.legendPosition === 'left' || config.legendPosition === 'right') ? 'flex-start' : 'center'
              }}>
                <div className="legend-item">
                  <span className="legend-color legend-high"></span>
                  <span className="legend-text">High Risks: {riskData.highRiskCount}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color legend-moderate"></span>
                  <span className="legend-text">Medium Risks: {riskData.lowRiskCount}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Risk Status Message */}
      {showRiskStatus && (
        <div className="risk-status-section">
          <p className="risk-status-text">
            Environment is at a{' '}
            <span className={`risk-status-level risk-status-${riskData.statusColor}`}>
              {riskData.statusMessage}
            </span>
          </p>
        </div>
      )}


      {/* Business Protection Text */}
      {showBusinessProtection && (
        <div className="business-protection-section">
          <p className="protection-header">
            <strong>Addressing your network risks isn't just about IT—it's about protecting your business.</strong>
          </p>
          <p className="protection-body">
            A single cyberattack can disrupt operations, damage your reputation, and result in thousands—or even hundreds of thousands—of dollars in losses. By identifying and addressing these risks proactively, you're not only safeguarding sensitive data and maintaining compliance, but also reinforcing the foundation for secure growth, client confidence, and long-term business success.
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// UTILITY FUNCTIONS FOR DATA EXTRACTION
// ============================================================================

/**
 * Extract findings status for specific items
 * Uses the same findings array as Key Findings Summary (data.findings)
 * Maps item names to their status (red/yellow/green/gray) based on findings status
 */


/**
 * Check if an item name matches a finding
 */


/**
 * Helper to replace variables in note templates
 * Supported variables: {count}, {total}, {percent}
 */
const replaceNoteVariables = (template, counts) => {
  if (!template || !counts) return template;
  let note = template;
  if (counts.count !== undefined) {
    note = note.replace(/\{\{\s*count\s*\}\}/gi, counts.count);
    note = note.replace(/\{\s*count\s*\}/gi, counts.count);
  }
  if (counts.total !== undefined) {
    note = note.replace(/\{\{\s*total\s*\}\}/gi, counts.total);
    note = note.replace(/\{\s*total\s*\}/gi, counts.total);
  }
  if (counts.percent !== undefined) {
    note = note.replace(/\{\{\s*percent\s*\}\}/gi, counts.percent);
    note = note.replace(/\{\s*percent\s*\}/gi, counts.percent);
  }
  return note;
};

/**
 * Extract key findings summary with compliance percentages
 * EXACTLY matches the backend DrawFinding function from New_template.js:162-239
 *
 * Backend structure (from DrawFinding):
 * findings array contains: [Category, Status, Compliance%, Notes, cost, counts]
 * - Filter items where item[2] != null && parseInt(item[2]) > ACCEPTABLE
 * - Status values: 'Healthy', 'Warning', 'Critical'
 * - Compliance is the percentage value (for Backups, it's % WITHOUT backups)
 * - Notes describe the specific issues found
 * 
 * Strategy:
 * 1. First check if backend provides findings array (data.findings)
 * 2. If not, recreate it using exact backend logic (Network_Backups_status, etc.)
 * 3. Filter and format for display
 */
export const extractKeyFindingsSummary = (data, customThresholdNotes = {}) => {
  if (!data) return [];

  // 1. Use existing findings if available (pre-processed by backend)
  let rowsPresent = data.findings || [];
  
  // 2. Fallback to recreating from raw scan data
  if (!Array.isArray(rowsPresent) || rowsPresent.length === 0) {
    rowsPresent = recreateFindingsArray(data);
  }

  
    // Map found rows by category name for easy lookup
  const foundMap = {};
  rowsPresent.forEach(finding => {
    const category = finding[0] || 'Unknown';
    const status = finding[1];
    const complianceRaw = finding[2];
    const notes = finding[3] || '';
    const counts = finding[5] || {}; // Extra data for variable replacement

    let compliance = 0;
    if (typeof complianceRaw === 'string') {
      compliance = parseInt(complianceRaw.replace('%', '')) || 0;
    } else {
      compliance = parseInt(complianceRaw) || 0;
    }

    const effectiveStatus = getFindingStatus(category, compliance, data.category_thresholds, data.acceptable_score_threshold || 20);
    
    // Fallback: If counts are missing (e.g. backend-provided findings), try to extract from notes
    if (Object.keys(counts).length === 0) {
      const match = notes.match(/(\d+)/);
      if (match) {
        counts.count = parseInt(match[1]);
        if (compliance > 0) {
          counts.total = Math.round((counts.count * 100) / compliance);
        } else {
          counts.total = counts.count;
        }
      } else if (compliance === 0) {
        counts.count = 0;
        counts.total = 100; // Best guess for "Total" if all are healthy
      }
      counts.percent = compliance;
    }
    
    // Determine status color. Trust the finding's status field first (it correctly uses
    // propagation logic like Red > Yellow > Green). Only fall back to effectiveStatus 
    // (computed from compliance %) if the finding has no explicit status.
    let statusColor = 'green';
    const resolvedStatus = status || effectiveStatus;
    if (resolvedStatus === 'Critical') {
      statusColor = 'red';
    } else if (resolvedStatus === 'Warning' || resolvedStatus === 'At Risk') {
      statusColor = 'yellow';
    } else if (resolvedStatus === 'Healthy') {
      statusColor = 'green';
    }

    // Special case: PII and other "detection" scans should show "No Data" (gray) 
    // when nothing is found (0% compliance), unlike inventory scans.
    if (compliance === 0 && statusColor === 'green' && (category === 'PII' || category === 'Personally Identifiable Information')) {
      statusColor = 'gray';
    }

    // Apply custom threshold notes if available
    let finalNotes = notes;
    
    // 1. Try notes passed via explicit argument
    const customCategoryNotes = customThresholdNotes[category];
    let customNoteTemplate = null;
    const statusKey = statusColor === 'red' ? 'Critical' : (statusColor === 'yellow' ? 'Warning' : 'Healthy');
    
    if (customCategoryNotes && customCategoryNotes[statusKey]) {
      customNoteTemplate = customCategoryNotes[statusKey];
    } 
    // 2. Fallback: Try notes embedded in data.category_thresholds (preferred source from backend)
    else if (data.category_thresholds) {
      const tierMap = { Healthy: 'Good', Warning: 'Moderate', Critical: 'Bad' };
      const tier = tierMap[statusKey];
      
      // Robust lookup in category_thresholds (same as in getFindingStatus)
      const cleanName = category?.trim();
      let thresholds = data.category_thresholds[cleanName];
      if (!thresholds) {
        const lowerName = cleanName?.toLowerCase();
        const match = Object.entries(data.category_thresholds).find(([k]) => k.trim().toLowerCase() === lowerName);
        if (match) thresholds = match[1];
      }
      
      if (thresholds?.rules?.[tier]?.note) {
        customNoteTemplate = thresholds.rules[tier].note;
      }
    }

    if (customNoteTemplate) {
      finalNotes = replaceNoteVariables(customNoteTemplate, {
        count: counts.count,
        total: counts.total,
        percent: compliance
      });
    }

    // Merging Logic: If this category already exists in the map, combine them
    // prioritising the most severe status (propagation logic: Red > Yellow > Green)
    if (foundMap[category]) {
      const existing = foundMap[category];
      const severityOrder = { 'red': 3, 'yellow': 2, 'green': 1, 'gray': 0 };
      
      // 1. Upgrade color/status if new one is more severe
      if (severityOrder[statusColor] > severityOrder[existing.statusColor]) {
        existing.statusColor = statusColor;
        existing.status = (statusColor === 'gray') ? 'No Data' : (status || effectiveStatus);
      }
      
      // 2. Sum counts/totals for recalculated compliance
      const existingCount = existing._rawCounts?.count || 0;
      const existingTotal = existing._rawCounts?.total || 0;
      const newCount = counts.count || 0;
      const newTotal = counts.total || 0;
      
      const combinedCount = existingCount + newCount;
      const combinedTotal = existingTotal + newTotal;
      
      existing._rawCounts = { count: combinedCount, total: combinedTotal };
      
      if (combinedTotal > 0) {
        existing.compliance = Math.round(((combinedTotal - combinedCount) / combinedTotal) * 100);
      }
      
      // 3. Append notes if they differ
      if (finalNotes && existing.notes !== finalNotes && !existing.notes.includes(finalNotes)) {
        existing.notes = `${existing.notes} | ${finalNotes}`;
      }
    } else {
      foundMap[category] = {
        category,
        status: (statusColor === 'gray') ? 'No Data' : (status || effectiveStatus),
        statusColor,
        compliance,
        notes: (statusColor === 'gray' && !finalNotes.includes('No PII detected')) ? 'No data available for this category.' : finalNotes,
        _rawCounts: { count: counts.count || 0, total: counts.total || 0 } // Internal reference for merging
      };
    }
  });



  // Return summaries in the MANDATORY_CATEGORIES order
  const finalSummaries = MANDATORY_CATEGORIES.map(cat => {
    if (foundMap[cat]) {
      return foundMap[cat];
    }
    // Return placeholder for missing mandatory categories
    return {
      category: cat,
      status: 'No Data',
      statusColor: 'gray',
      compliance: 0,
      notes: 'No data available for this category.'
    };
  });

  // Add any other found categories that weren't in the mandatory list
  Object.keys(foundMap).forEach(cat => {
    if (!MANDATORY_CATEGORIES.includes(cat)) {
      finalSummaries.push(foundMap[cat]);
    }
  });

  return finalSummaries;
};


/**
 * Ported from backend: evaluateThreshold
 * Evaluates a finding's severity based on value and thresholds.
 */
function evaluateThreshold(value, valueType, rules) {
  if (!rules || typeof rules !== 'object') {
    return 'Unknown';
  }

  if (valueType === 'boolean') {
    if (rules.Good && rules.Good.value === value) return 'Good';
    if (rules.Bad && rules.Bad.value === value) return 'Bad';
    if (rules.Moderate && rules.Moderate.value === value) return 'Moderate';
    return 'Unknown';
  }

  if (valueType === 'numeric' || !valueType) {
    const val = parseFloat(value);
    if (isNaN(val)) return 'Unknown';

    const tiers = ['Bad', 'Moderate', 'Good'];
    for (const tier of tiers) {
      const rule = rules[tier];
      if (!rule) continue;

      let matches = false;
      switch (rule.operator) {
        case '<': matches = val < rule.value; break;
        case '<=': matches = val <= rule.value; break;
        case '>': matches = val > rule.value; break;
        case '>=': matches = val >= rule.value; break;
        case '==': matches = val == rule.value; break;
        case 'Between': matches = val >= rule.lower && val <= rule.upper; break;
      }
      if (matches) return tier;
    }
  }

  return 'Unknown';
}

/**
 * Ported from backend: getFindingStatus
 * Returns mapping: "Healthy", "Warning", or "Critical"
 */
function getFindingStatus(categoryName, value, category_thresholds, ACCEPTABLE) {
  if (category_thresholds) {
    // Robust lookup: try exact match first, then case-insensitive + trimmed
    const cleanName = categoryName?.trim();
    let thresholds = category_thresholds[cleanName];
    
    if (!thresholds) {
      const lowerName = cleanName?.toLowerCase();
      const match = Object.entries(category_thresholds).find(([k]) => k.trim().toLowerCase() === lowerName);
      if (match) thresholds = match[1];
    }

    if (thresholds) {
      const rules = thresholds.rules;
      if (rules) {
        const result = evaluateThreshold(value, thresholds.type || thresholds.value_type || 'numeric', rules);
        if (result === 'Good') return 'Healthy';
        if (result === 'Moderate') return 'Warning';
        if (result === 'Bad') return 'Critical';
      }
    }
  }
  // Fallback to basic logic
  return value > ACCEPTABLE ? 'Warning' : 'Healthy';
}

/**
 * Recreate findings array using exact backend logic
 * Matches create_network_report, create_ad_report, etc. from template2
 * Returns array in format: [[Category, Status, Compliance%, Notes, cost], ...]
 */
export function recreateFindingsArray(data) {
  if (!data) return [];
  const ACCEPTABLE = data?.acceptable_score_threshold ?? data?.scanData?.acceptable_score_threshold ?? 20;

  // 0. Use pre-calculated findings if they exist (highest fidelity)
  const preCalculated = data?.findings || data?.scanData?.findings || data?.data?.findings || [];
  const findings = Array.isArray(preCalculated) ? [...preCalculated] : [];

  // Multi-layered fallback for scan data source
  let scanDataSource = data?.scanData || data?.data || data;

  // Robust check for nested data (if we are looking at a full report payload instead of just metrics)
  if (scanDataSource && scanDataSource.data && typeof scanDataSource.data === 'object' && !Array.isArray(scanDataSource.data)) {
      // If scanDataSource has keys characteristic of a processed report (exportData, coverData, findings)
      // and also has a 'data' key, the raw metrics are likely inside that 'data' key.
      const keys = Object.keys(scanDataSource);
      if (keys.includes('exportData') || keys.includes('findings') || keys.includes('coverData')) {
          console.log('[recreateFindingsArray] Found nested report structure, delving into .data for metrics');
          scanDataSource = scanDataSource.data;
      }
  }

  console.log('[recreateFindingsArray] Data structure:', {
    hasScanData: !!data.scanData,
    hasData: !!data.data,
    scanDataSourceKeys: scanDataSource ? Object.keys(scanDataSource) : [],
    hasNetworkDevices: !!scanDataSource?.network_devices,
    networkDevicesCount: scanDataSource?.network_devices?.length || 0,
    hasMS365: !!scanDataSource?.MS365
  });

  if (!scanDataSource) {
    console.log('[recreateFindingsArray] No scan data source found');
    return findings;
  }

  // Process Network findings (matches create_network_report logic)
  if (scanDataSource.network_devices) {
    console.log('[recreateFindingsArray] Processing network_devices');
    
    const backupFinding = calculateBackupsFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (backupFinding) {
      findings.push(backupFinding);
    }

    const avFinding = calculateNetworkAntivirusFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (avFinding) {
      findings.push(avFinding);
    }

    const encryptionFinding = calculateNetworkEncryptionFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (encryptionFinding) {
      findings.push(encryptionFinding);
    }

    const firewallFinding = calculateNetworkFirewallFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (firewallFinding) {
      findings.push(firewallFinding);
    }

    const portsFinding = calculateNetworkOpenPortsFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (portsFinding) {
      findings.push(portsFinding);
    }

    const osFinding = calculateNetworkOSFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (osFinding) {
      findings.push(osFinding);
    }

    const servicesFinding = calculateNetworkServicesFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (servicesFinding) {
      findings.push(servicesFinding);
    }

    const driveFinding = calculateNetworkDriveFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (driveFinding) {
      findings.push(driveFinding);
    }

    const ageFinding = calculateNetworkAgeFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (ageFinding) {
      findings.push(ageFinding);
    }

    const softwareFinding = calculateNetworkSoftwaresFinding(scanDataSource.network_devices, ACCEPTABLE, data.category_thresholds);
    if (softwareFinding) {
      findings.push(softwareFinding);
    }
  }

  // Process Localhost findings
  const localhostData = scanDataSource.localhost || scanDataSource['Localhost Scan'] || scanDataSource['Localhost'];
  if (localhostData) {
    console.log('[recreateFindingsArray] Processing localhost data');
    const localhostFindings = calculateLocalhostFindings(localhostData, ACCEPTABLE, data.category_thresholds);
    if (localhostFindings && localhostFindings.length > 0) {
      findings.push(...localhostFindings);
    }
  }

  // Process AD findings
  const adData = scanDataSource['Active Directory Scan'] || scanDataSource['AD Scan'] || scanDataSource['Ad Scan'] || scanDataSource['Ad_Scan'];
  if (adData) {
    console.log('[recreateFindingsArray] Processing AD data');
    const adFindings = calculateADFindings(adData, ACCEPTABLE, data.category_thresholds);
    if (adFindings && adFindings.length > 0) {
      findings.push(...adFindings);
    }
  }

  // Process MS365 findings
  const ms365Data = scanDataSource.MS365 || scanDataSource['MS365 Scan'] || scanDataSource['MS365'] || scanDataSource['MS365 Scan (Cover)'];
  if (ms365Data) {

    console.log('[recreateFindingsArray] Processing MS365 data');
    const ms365Findings = calculateMS365Findings(ms365Data, ACCEPTABLE, data.category_thresholds);
    if (ms365Findings && ms365Findings.length > 0) {
      findings.push(...ms365Findings);
    }
  }

  // Process Dark Web findings
  const hibpData = scanDataSource.HIPB_result || scanDataSource.HIBP || scanDataSource['Dark Web'];
  if (hibpData) {
    console.log('[recreateFindingsArray] Processing Dark Web data');
    const darkWebFindings = calculateDarkWebFindings(hibpData, ACCEPTABLE, data.category_thresholds);
    if (darkWebFindings && darkWebFindings.length > 0) {
      findings.push(...darkWebFindings);
    }
  }

  // Process PII findings
  const piiData = scanDataSource.PII_result || scanDataSource.PII || scanDataSource['PII Scan'] || scanDataSource['PII'];
  if (piiData) {
    console.log('[recreateFindingsArray] Processing PII data');
    const piiFindings = calculatePIIFindings(piiData, ACCEPTABLE, data.category_thresholds);
    if (piiFindings && piiFindings.length > 0) {
      findings.push(...piiFindings);
    }
  }

  // NEW: Robust Findings Merging Logic
  // This ensures that multiple scan types contributing to the same category (e.g. 'Age') 
  // correctly propagate the most severe status (Red > Yellow > Green)
  const mergedMap = new Map();
  const severityOrder = { 'Critical': 3, 'Warning': 2, 'Healthy': 1, 'Unknown': 0 };

  findings.forEach(f => {
    const [name, status, compliance, notes, cost, details, scanType] = f;
    if (!mergedMap.has(name)) {
      mergedMap.set(name, [...f]);
    } else {
      const existing = mergedMap.get(name);
      
      // 1. Upgrade Status if new one is more severe
      if (severityOrder[status] > severityOrder[existing[1]]) {
        existing[1] = status;
      }
      
      // 2. Append notes if they differ
      if (notes && existing[3] !== notes && !existing[3].includes(notes)) {
        existing[3] = `${existing[3]} | ${notes}`;
      }
      
      // 3. Preserve scanType if missing
      if (!existing[6] && scanType) {
        existing[6] = scanType;
      }
      
      // 4. Sum counts/totals if details exist
      if (details && existing[5]) {
        existing[5].count = (existing[5].count || 0) + (details.count || 0);
        existing[5].total = (existing[5].total || 0) + (details.total || 0);
        
        // 4. Recalculate compliance based on updated total/count (if percentage-based)
        if (existing[5].total > 0) {
          existing[2] = Math.round(((existing[5].total - existing[5].count) / existing[5].total) * 100);
          existing[5].percent = existing[2];
        }
      }
      
      // 5. Sum cost
      existing[4] = (existing[4] || 0) + (cost || 0);
    }
  });

  const finalFindings = Array.from(mergedMap.values());
  console.log('[recreateFindingsArray] Final merged findings:', finalFindings.length);
  return finalFindings;
}

/**
 * Calculate Localhost findings from raw data
 * Returns array of [Category, Status, Compliance%, Notes, cost]
 */
function calculateLocalhostFindings(localhost, ACCEPTABLE, category_thresholds) {
  const findings = [];
  
  // 1. Firewall (matches logic from extractLocalhostFirewallStatus)
  try {
    const firewall = localhost["System"]?.Firewall || localhost.Firewall;
    if (firewall) {
      const domain = firewall.Domain === true;
      const privateNet = firewall.Private === true;
      const publicNet = firewall.Public === true;
      const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;
      
      const compliance = Math.round(((3 - enabledCount) / 3) * 100);
      const categoryName = 'Firewall (Localhost Scan)';
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = enabledCount === 3 ? 'Firewall is fully enabled.' : `Firewall is not fully enabled. Enabled profiles: ${[
        domain ? 'Domain' : null,
        privateNet ? 'Private' : null,
        publicNet ? 'Public' : null
      ].filter(Boolean).join(', ') || 'None'}`;
      
      findings.push([categoryName, status, compliance, notes, 0, { count: 3 - enabledCount, total: 3, percent: compliance }, 'Localhost Scan']);
    }
  } catch (e) { console.log('Error calculating localhost firewall finding', e); }

  // 2. Antivirus
  try {
    const antivirus = localhost["System"]?.["Antivirus"] || localhost.Antivirus;
    if (antivirus) {
      const isEnabled = antivirus.Status === 'Enabled' || antivirus.state === 'Enabled' || antivirus === 'Enabled';
      const compliance = isEnabled ? 0 : 100;
      const status = getFindingStatus('Antivirus', compliance, category_thresholds, ACCEPTABLE);
      const notes = isEnabled ? 'Antivirus is enabled.' : 'Antivirus is disabled or not found.';
      findings.push(['Antivirus (Localhost Scan)', status, compliance, notes, 0, { count: isEnabled ? 0 : 1, total: 1, percent: compliance }, 'Localhost Scan']);
    }
  } catch (e) { console.log('Error calculating localhost antivirus finding', e); }

  // 3. Encryption
  try {
    const encryptionFinding = calculateLocalhostEncryptionFinding(localhost, ACCEPTABLE, category_thresholds);
    if (encryptionFinding) {
      findings.push(encryptionFinding);
    }
  } catch (e) { console.log('Error calculating localhost encryption finding', e); }

  return findings;
}

/**
 * Calculate Encryption finding for localhost
 */
export function calculateLocalhostEncryptionFinding(localhost, ACCEPTABLE, category_thresholds) {
  const encryption = localhost?.System?.Encryption || 
                     localhost?.["Hardware Informations"]?.Encryption || 
                     localhost?.Encryption || 
                     localhost?.encryption ||
                     localhost?.System?.encryption ||
                     localhost?.["Hardware Informations"]?.encryption;
  
  if (encryption === undefined) return null;

  let isEncrypted = false;
  
  if (typeof encryption === 'string') {
    const encLower = encryption.toLowerCase();
    if (!encLower.includes('disabled') && !encLower.includes('off') && !encLower.includes('not enabled')) {
      isEncrypted = true;
    }
  } else if (Array.isArray(encryption)) {
    // Standard array-of-arrays format: [['C:', 'Status (Encrypted)'], ...]
    for (const enc of encryption) {
      const status = (Array.isArray(enc) ? enc[1] : enc) || '';
      if (status.toLowerCase().includes('(encrypted)')) {
        isEncrypted = true;
        break;
      }
    }
  } else if (typeof encryption === 'object' && encryption !== null) {
    // Hardware object format: { enabled: true, type: "BitLocker" }
    if (encryption.enabled === true || encryption.status === 'Enabled' || encryption.Enabled === true) {
      isEncrypted = true;
    }
  }

  const compliance = isEncrypted ? 0 : 100;
  const status = getFindingStatus('Encryption', compliance, category_thresholds, ACCEPTABLE);
  const notes = isEncrypted ? 'Disk encryption is enabled.' : 'Disk encryption is disabled or not found.';
  
  return ['Encryption', status, compliance, notes, 0, { count: isEncrypted ? 0 : 1, total: 1, percent: compliance }, 'Localhost Scan'];
}

/**
 * Calculate AD findings from raw data
 * Returns array of [Category, Status, Compliance%, Notes, cost]
 */
function calculateADFindings(ad, ACCEPTABLE, category_thresholds) {
  const findings = [];
  
  // 1. Password Policy (matches create_ad_report logic)
  try {
    const passwordPolicy = ad["Account Policy"] || ad.AccountPolicy;
    if (passwordPolicy) {
      const minAge = parseInt(passwordPolicy["Minimum password age"]);
      const maxAge = parseInt(passwordPolicy["Maximum password age"]);
      const isWeak = minAge === 0 || maxAge === 0 || maxAge > 90;
      const compliance = isWeak ? 100 : 0;
      const categoryName = 'GPO'; // Best match for AD Policy in thresholds
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = isWeak ? 'Password policies are weak or non-expiring.' : 'Password policies are properly configured.';
      findings.push([categoryName, status, compliance, notes, 0, { count: isWeak ? 1 : 0, total: 1, percent: compliance }, 'AD Scan']);
    }
  } catch (e) { console.log('Error calculating AD policy finding', e); }

  // 2. Accounts (AD Users)
  try {
    const users = ad["AD Users"] || ad.Users;
    if (users && Array.isArray(users)) {
      const inactiveCount = users.filter(u => u.Enabled === false || u.Enabled === 'False' || u.State === 'Disabled').length;
      const compliance = users.length > 0 ? Math.round((inactiveCount / users.length) * 100) : 0;
      const categoryName = 'AD Users';
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = inactiveCount > 0 ? `${inactiveCount} inactive or disabled AD accounts found.` : 'No inactive AD accounts found.';
      findings.push([categoryName, status, compliance, notes, 0, { count: inactiveCount, total: users.length, percent: compliance }, 'AD Scan']);
    }
  } catch (e) { console.log('Error calculating AD accounts finding', e); }

  // 3. AD Computers
  try {
    const computers = ad["AD Computers"] || ad.Computers;
    if (computers && Array.isArray(computers)) {
      const inactiveCount = computers.filter(c => c.Enabled === false || c.Enabled === 'False' || c.State === 'Disabled').length;
      const compliance = computers.length > 0 ? Math.round((inactiveCount / computers.length) * 100) : 0;
      const categoryName = 'AD Computers';
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = inactiveCount > 0 ? `${inactiveCount} inactive or disabled AD computer accounts found.` : 'No inactive AD computer accounts found.';
      findings.push([categoryName, status, compliance, notes, 0, { count: inactiveCount, total: computers.length, percent: compliance }, 'AD Scan']);
    }
  } catch (e) { console.log('Error calculating AD computers finding', e); }

  // 4. OU Analysis
  try {
    const ouList = ad["OU_list"] || ad.OU_list;
    if (ouList && Array.isArray(ouList)) {
      const riskCount = ouList.filter(ou => ou.Risk === 'High' || ou.Risk === 'Medium').length;
      const compliance = ouList.length > 0 ? Math.round((riskCount / ouList.length) * 100) : 0;
      const categoryName = 'OU';
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = riskCount > 0 ? `${riskCount} OUs with risk issues detected.` : 'All OUs are properly configured.';
      findings.push([categoryName, status, compliance, notes, 0, { count: riskCount, total: ouList.length, percent: compliance }, 'AD Scan']);
    }
  } catch (e) { console.log('Error calculating AD OU finding', e); }

  // 5. AD Groups
  try {
    const groups = ad["AD Groups"] || ad.Groups;
    if (groups && Array.isArray(groups)) {
      const riskCount = groups.filter(g => g.Risk === 'High' || g.Risk === 'Medium' || g.Members === 0).length;
      const compliance = groups.length > 0 ? Math.round((riskCount / groups.length) * 100) : 0;
      const categoryName = 'AD Groups';
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = riskCount > 0 ? `${riskCount} unmanaged or empty AD security groups found.` : 'All AD security groups are properly managed.';
      findings.push([categoryName, status, compliance, notes, 0, { count: riskCount, total: groups.length, percent: compliance }, 'AD Scan']);
    }
  } catch (e) { console.log('Error calculating AD groups finding', e); }

  // 6. FSMO Analysis
  try {
    const fsmo = ad["FSMO_roles"] || ad.FSMO;
    if (fsmo && Array.isArray(fsmo)) {
      const riskCount = fsmo.filter(f => f.Risk === 'High' || f.Risk === 'Medium').length;
      const compliance = fsmo.length > 0 ? Math.round((riskCount / fsmo.length) * 100) : 0;
      const categoryName = 'FSMO';
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = riskCount > 0 ? `${riskCount} FSMO roles with potential issues detected.` : 'All FSMO roles are healthy.';
      findings.push([categoryName, status, compliance, notes, 0, { count: riskCount, total: fsmo.length, percent: compliance }, 'AD Scan']);
    }
  } catch (e) { console.log('Error calculating AD FSMO finding', e); }

  // 7. Admins Analysis
  try {
    const admins = ad["Domain Admins"] || ad.Admins;
    if (admins && Array.isArray(admins)) {
      const expIssueCount = admins.filter(a => a.PasswordNeverExpires === true || a.PasswordNeverExpires === 'True').length;
      const compliance = admins.length > 0 ? Math.round((expIssueCount / admins.length) * 100) : 0;
      const categoryName = 'Admins';
      const status = getFindingStatus(categoryName, compliance, category_thresholds, ACCEPTABLE);
      const notes = expIssueCount > 0 ? `${expIssueCount} admin accounts with non-expiring passwords.` : 'All admin accounts have expiring passwords.';
      findings.push([categoryName, status, compliance, notes, 0, { count: expIssueCount, total: admins.length, percent: compliance }, 'AD Scan']);
    }
  } catch (e) { console.log('Error calculating AD Admins finding', e); }

  // 8. Age Analysis (renamed from Workstation Age for consistency)
  try {
    const ageFinding = calculateADWorkstationAgeFinding(ad, ACCEPTABLE, category_thresholds);
    if (ageFinding) {
      findings.push(ageFinding);
    }
  } catch (e) { console.log('Error calculating AD Age finding', e); }

  return findings;
}

/**
 * Calculate MS365 findings from raw data
 * Returns array of [Category, Status, Compliance%, Notes, cost]
 */
function calculateMS365Findings(ms365, ACCEPTABLE, category_thresholds) {
  const findings = [];
  
  // 1. MS Users (Inactive)
  try {
    const users = ms365.users || [];
    const inactive = ms365.inactive_accounts || [];
    if (users.length > 0) {
      const avg = Math.round(100 * inactive.length / users.length);
      const status = getFindingStatus('MS Users', avg, category_thresholds, ACCEPTABLE);
      const notes = inactive.length > 0 ? `${inactive.length} user${inactive.length > 1 ? 's are' : ' is'} inactive.` : 'All users are active.';
      findings.push(["MS Users", status, avg, notes, 0, { count: inactive.length, total: users.length, percent: avg }, 'MS365 Scan']);
    }
  } catch (e) { console.log('Error MS Users fallback', e); }

  // 2. MFA
  try {
    const mfaUsers = ms365.MFA || [];
    const total = ms365.users?.length || 0;
    if (total > 0) {
      const disabled = mfaUsers.filter(user => user["MFA Enabled"] === false);
      const avg = Math.round(100 * disabled.length / total);
      const status = getFindingStatus('MFA', avg, category_thresholds, ACCEPTABLE);
      const notes = disabled.length > 0 ? `${disabled.length} user${disabled.length > 1 ? 's are' : ' is'} with no MFA Enabled.` : 'MFA is enabled for all users.';
      findings.push(["MFA", status, avg, notes, 0, { count: disabled.length, total: total, percent: avg }, 'MS365 Scan']);
    }
  } catch (e) { console.log('Error MFA fallback', e); }

  // 3. MS Policy
  try {
    const policyKeys = ["password_policies", "legacy_auth_enabled", "conditional_access_policies", "lack_of_dlp_policies", "misconfigured_exchange_forwarding"];
    let disabledCount = 0;
    policyKeys.forEach(key => {
      const isIssue = key === "conditional_access_policies" ? ms365[key] !== 1 : ms365[key] !== 0;
      if (isIssue) disabledCount++;
    });
    if (ms365.hasOwnProperty('password_policies') || ms365.hasOwnProperty('legacy_auth_enabled')) {
      const avg = disabledCount > 0 ? 100 : 0;
      const status = getFindingStatus('MS Policy', avg, category_thresholds, ACCEPTABLE);
      const notes = disabledCount > 0 ? `${disabledCount} MS policies are misconfigured or disabled.` : 'All MS policies are properly configured.';
      findings.push(["MS Policy", status, avg, notes, 0, { count: disabledCount, total: policyKeys.length, percent: avg }, 'MS365 Scan']);
    }
  } catch (e) { console.log('Error MS Policy fallback', e); }

  // 4. Excessive Admin Privileges
  try {
    const excessiveAdmins = ms365.excessive_admin_privileges || [];
    const avg = excessiveAdmins.length > 0 ? 100 : 0;
    const categoryName = 'Excessive Admin Privileges';
    const status = getFindingStatus(categoryName, avg, category_thresholds, ACCEPTABLE);
    const notes = excessiveAdmins.length > 0 ? `${excessiveAdmins.length} users have excessive admin privileges.` : 'No excessive admin privileges detected.';
    findings.push([categoryName, status, avg, notes, 0, { count: excessiveAdmins.length, total: ms365.users?.length || 0, percent: avg }, 'MS365 Scan']);
  } catch (e) { console.log('Error Excessive Admin Privileges fallback', e); }

  // 5. Excessive Sharing
  try {
    const sharing = ms365.excessive_sharing_onedrive_sharepoint;
    if (sharing !== undefined) {
      const avg = (sharing === 1 || sharing === true) ? 100 : 0;
      const categoryName = 'Excessive Sharing';
      const status = getFindingStatus(categoryName, avg, category_thresholds, ACCEPTABLE);
      const notes = avg > 0 ? 'Excessive sharing is enabled on OneDrive/SharePoint.' : 'No excessive sharing detected.';
      findings.push([categoryName, status, avg, notes, 0, { count: avg === 100 ? 1 : 0, total: 1, percent: avg }, "MS365 Scan"]);
    }
  } catch (e) { console.log('Error Excessive Sharing fallback', e); }

  // 6. Data Retention Policy
  try {
    const retention = ms365.data_retention_policies_misconfigured;
    if (retention !== undefined) {
      const avg = (retention === 1 || retention === true) ? 100 : 0;
      const categoryName = 'Data Retention Policy';
      const status = getFindingStatus(categoryName, avg, category_thresholds, ACCEPTABLE);
      const notes = avg > 0 ? 'Data retention policies are misconfigured.' : 'Data retention policies are properly configured.';
      findings.push([categoryName, status, avg, notes, 0, { count: avg === 100 ? 1 : 0, total: 1, percent: avg }, "MS365 Scan"]);
    }
  } catch (e) { console.log('Error Data Retention fallback', e); }

  return findings;
}

/**
 * Calculate Dark Web findings from raw data
 */
function calculateDarkWebFindings(hibp, ACCEPTABLE, category_thresholds) {
  const findings = [];
  try {
    const breaches = hibp.breaches || [];
    const avg = breaches.length > 0 ? 100 : 0;
    const status = getFindingStatus('Dark Web', avg, category_thresholds, ACCEPTABLE);
    const notes = breaches.length > 0 ? `${breaches.length} security breaches found on the Dark Web for this domain.` : 'No security breaches found on the Dark Web.';
    findings.push(["Dark Web", status, avg, notes, 0, { count: breaches.length, total: breaches.length, percent: avg }, 'Dark Web Scan']);
  } catch (e) { console.log('Error Dark Web fallback', e); }
  return findings;
}

/**
 * Calculate PII findings from raw data
 */
function calculatePIIFindings(pii, ACCEPTABLE, category_thresholds) {
  const findings = [];
  try {
    const totalPII = pii.totalPII || pii.total_pii || pii.total || 0;
    const avg = totalPII > 0 ? 100 : 0;
    const status = getFindingStatus('PII', avg, category_thresholds, ACCEPTABLE);
    const notes = totalPII > 0 ? `${totalPII} PII items detected.` : 'No PII detected.';
    findings.push(["PII", status, avg, notes, 0, { count: totalPII, total: totalPII, percent: avg }, 'PII Scan']);
  } catch (e) { console.log('Error PII fallback', e); }
  return findings;
}

/**
 * Total findings created
 */

/**
 * Calculate Backups finding using EXACT backend Network_Backups_status logic
 * Returns: [Category, Status, Compliance%, Notes, cost] or null
 * Matches template2/Network.js:552-642 EXACTLY
 */
function calculateBackupsFinding(devices, configThreshold, category_thresholds) {
  console.log('[calculateBackupsFinding] Called with devices:', Array.isArray(devices) ? devices.length : 'not an array');

  if (!Array.isArray(devices)) {
    console.log('[calculateBackupsFinding] Devices is not an array:', typeof devices);
    return null;
  }

  const ACCEPTABLE = configThreshold !== undefined ? configThreshold : 20;
  const detail = false; // We're not in detail mode for Key Findings Summary

  // Use the same helper function logic as backend
  const values = _Network_Backups_status_helper(devices);
  const rows = values[0];
  const nobackup = values[1];
  const backups_count = values[2];
  const percent = values[3];

  console.log('[calculateBackupsFinding] Values from helper:', {
    rows: rows.length,
    nobackup,
    backups_count,
    percent
  });

  // Early returns match backend logic exactly
  if (rows.length === 0 || backups_count === 0) {
    console.log('[calculateBackupsFinding] Early return: no backup data');
    return null;
  }

  // Use dynamic thresholds
  const Status = getFindingStatus('Backups', percent, category_thresholds, ACCEPTABLE);

  // Still support skipping "Healthy" items if in filtered view, but reconstruct everything for the array
  // KeyFindingsSummaryWidget will handle the final presentation filtering
  const Notes = nobackup > 0 ? `${nobackup} devices are running without recent backups.` : 'All devices are properly backed up.';
  const avg = percent;

  console.log('[calculateBackupsFinding] Result:', { Status, avg, Notes });
  return ['Backups', Status, avg, Notes, 0, { count: nobackup, total: backups_count, percent: avg }, 'Network Scan'];
}

/**
 * Helper function that matches _Network_Backups_status from template2/Network.js:528-550
 * Returns: [rows, nobackup, backups_count, avg]
 */
function _Network_Backups_status_helper(devices) {
  const rows = [];
  let nobackup = 0;
  let total = devices.length;
  let backups_count = 0;

  console.log('[Backups] Processing devices:', total);
  console.log('[Backups] Sample device structure:', devices[0] ? {
    hasSystem: !!devices[0].system,
    systemKeys: devices[0].system ? Object.keys(devices[0].system) : [],
    hasBackups: !!devices[0]?.system?.['backups'],
    backupsKeys: devices[0]?.system?.['backups'] ? Object.keys(devices[0].system['backups']) : []
  } : 'No devices');

  try {
    for (const device of devices) {
      const backups = device?.system?.['backups'];
      if (!backups) {
        console.log('[Backups] Device', device.ip || device.hostname, 'has no backups data');
        continue;
      }

      console.log('[Backups] Processing device:', device.ip, {
        last_backup: backups['last_backup'],
        status: backups['status'],
        notes: backups['notes']
      });

      rows.push([
        device.ip,
        backups['last_backup'] || '-',
        backups['status'] || '-',
        backups['notes'] || '-'
      ]);

      // Check if backup is completed (matches backend logic exactly)
      const notes = backups['notes'] || '';
      const isCompleted = notes.includes("Backup completed.");
      console.log('[Backups] Device', device.ip, 'backup completed:', isCompleted, 'notes:', notes);

      if (!isCompleted) {
        nobackup++;
      }
      backups_count++;
    }
  } catch (error) {
    console.error("Error _Network_Backups_status_helper", error);
  }

  const avg = backups_count > 0 ? Math.round(100 * nobackup / backups_count) : 0;
  console.log('[Backups] Summary:', {
    total_devices: total,
    devices_with_backup_data: backups_count,
    devices_without_backups: nobackup,
    percentage: avg
  });

  return [rows, nobackup, backups_count, avg];
}

/**
 * Calculate Antivirus finding for network devices
 */
export function calculateNetworkAntivirusFinding(devices, ACCEPTABLE, category_thresholds) {
  const [notfound, total, avg] = _Network_Antivirus_status_helper(devices);
  const status = getFindingStatus('Antivirus', avg, category_thresholds, ACCEPTABLE);
  const notes = notfound > 0 
    ? `${notfound} device${notfound > 1 ? 's' : ''} have outdated or missing antivirus.` 
    : 'All devices have up-to-date antivirus protection.';
  return ['Antivirus (Network Scan)', status, avg, notes, 0, { count: notfound, total: total, percent: avg }, 'Network Scan'];
}

/**
 * Helper for Antivirus status
 */
function _Network_Antivirus_status_helper(devices) {
  // Exact match of _Network_Antivirus_status from Network.js L193-L233
  let notfound = 0;
  const total = devices.length || 0;

  try {
    for (const device of devices) {
      // Use uppercase key to match actual scan data structure (from Network.js)
      const avs = device?.system?.['Antivirus'];

      // Missing or empty antivirus data = device is not protected
      if (!Array.isArray(avs) || avs.length === 0) {
        notfound++;
        continue;
      }
    }
  } catch (error) {
    console.log('Error _Network_Antivirus_status_helper', error);
  }

  const avg = total > 0 ? Math.round(100 * notfound / total) : 0;
  return [notfound, total, avg];
}

/**
 * Calculate Encryption finding for network devices
 */
export function calculateNetworkEncryptionFinding(devices, ACCEPTABLE, category_thresholds) {
  const [notEncrypted, total, avg] = _Network_Encryption_status_helper(devices);
  const status = getFindingStatus('Encryption', avg, category_thresholds, ACCEPTABLE);
  const notes = notEncrypted > 0 
    ? `${notEncrypted} device${notEncrypted > 1 ? 's' : ''} are not fully encrypted.` 
    : 'All devices are properly encrypted.';
  return ['Encryption (Network Scan)', status, avg, notes, 0, { count: notEncrypted, total: total, percent: avg }, 'Network Scan'];
}

/**
 * Helper for Encryption status
 */
function _Network_Encryption_status_helper(devices) {
  // Exact match of _Network_Encryption_status from Network.js L235-L278
  let notEncryptedCount = 0;
  const totalDevices = devices.length || 0;

  for (const device of devices) {
    const encryptionArray = device?.system?.Encryption;
    // Skip devices where Encryption key is completely undefined (not scanned)
    if (encryptionArray == undefined) {
      continue;
    }
    // Empty array or not an array = not encrypted
    if (!Array.isArray(encryptionArray) || encryptionArray.length === 0) {
      notEncryptedCount++;
      continue;
    }

    let isEncrypted = false;
    for (const enc of encryptionArray) {
      // enc is an array like [driveLetter, "BitLocker (Encrypted)"]
      const status = enc[1] || '';
      if (status.toLowerCase().includes('(encrypted)')) {
        isEncrypted = true;
      }
    }

    if (!isEncrypted) {
      notEncryptedCount++;
    }
  }

  const avg = totalDevices > 0 ? Math.round(100 * notEncryptedCount / totalDevices) : 0;
  return [notEncryptedCount, totalDevices, avg];
}

/**
 * Calculate Firewall finding for network devices
 */
export function calculateNetworkFirewallFinding(devices, ACCEPTABLE, category_thresholds) {
  const [notFullyEnabled, total, avg] = _Network_Firewall_status_helper(devices);
  const status = getFindingStatus('Firewall (Network Scan)', avg, category_thresholds, ACCEPTABLE);
  const notes = notFullyEnabled > 0 
    ? `${notFullyEnabled} device${notFullyEnabled > 1 ? 's' : ''} have firewall disabled or partially enabled.` 
    : 'All devices have firewall fully enabled.';
  return ['Firewall (Network Scan)', status, avg, notes, 0, { count: notFullyEnabled, total: total, percent: avg }, 'Network Scan'];
}

/**
 * Helper for Firewall status
 */
function _Network_Firewall_status_helper(devices) {
  let notFullyEnabled = 0;
  const totalDevices = devices.length || 0;

  for (const device of devices) {
    const firewall = device?.system?.Firewall || device?.system?.firewall;

    if (!firewall || typeof firewall !== 'object') {
      notFullyEnabled++;
      continue;
    }

    const domain = firewall.Domain === true || firewall.domain === true;
    const privateNet = firewall.Private === true || firewall.private === true;
    const publicNet = firewall.Public === true || firewall.public === true;
    const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;

    if (enabledCount < 3) {
      notFullyEnabled++;
    }
  }

  const avg = totalDevices > 0 ? Math.round(100 * notFullyEnabled / totalDevices) : 0;
  return [notFullyEnabled, totalDevices, avg];
}

/**
 * Calculate Open Ports finding for network devices
 */
export function calculateNetworkOpenPortsFinding(devices, ACCEPTABLE, category_thresholds) {
  const [sensitive, total, avg] = _Network_OpenPorts_status_helper(devices);
  const status = getFindingStatus('Open Ports (Network Scan)', avg, category_thresholds, ACCEPTABLE);
  const notes = sensitive > 0 
    ? `${sensitive} device${sensitive > 1 ? 's' : ''} have sensitive ports open.` 
    : 'No sensitive open ports detected.';
  return ['Open Ports (Network Scan)', status, avg, notes, 0, { count: sensitive, total: total, percent: avg }, 'Network Scan'];
}

/**
 * Helper for Open Ports status
 */
function _Network_OpenPorts_status_helper(devices) {
  const sensitive_ports = {
    21: "FTP", 22: "SSH", 23: "Telnet", 25: "SMTP",
    53: "DNS", 80: "HTTP", 139: "NetBIOS", 143: "SMB",
    3306: "MySQL", 3389: "RDP", 8080: "HTTP"
  };
  const sensitive_devices = new Set();
  const total = devices.length || 0;

  for (const device of devices) {
    const open_ports = device['open_ports'] || device['Open Ports'] || {};
    const keys = Object.keys(open_ports);
    for (const port of keys) {
      if (sensitive_ports[port]) {
        sensitive_devices.add(device.ip || device.hostname || device.name);
        break;
      }
    }
  }

  const avg = total > 0 ? Math.round(100 * sensitive_devices.size / total) : 0;
  return [sensitive_devices.size, total, avg];
}

/**
 * Calculate OS finding for network devices
 */
export function calculateNetworkOSFinding(devices, ACCEPTABLE, category_thresholds) {
  const [outdated, total, avg] = _Network_OS_status_helper(devices);
  const status = getFindingStatus('OS (Network Scan)', avg, category_thresholds, ACCEPTABLE);
  const notes = outdated > 0 
    ? `${outdated} device${outdated > 1 ? 's' : ''} are running moderately supported operating systems.` 
    : 'All devices are running supported operating systems.';
  return ['OS (Network Scan)', status, avg, notes, 0, { count: outdated, total: total, percent: avg }, 'Network Scan'];
}

/**
 * Helper for OS status
 */
function _Network_OS_status_helper(devices) {
  let outdated = 0;
  let total = 0;
  try {
    for (const device of devices) {
      const OS = device?.system?.['OS'] || device?.system?.os;
      if (!OS) continue;
      if (/Windows XP|Vista|Windows 7|Windows 8(?!\.1)|Server 2003|Server 2008|Server 2012/.test(OS)) {
        outdated++;
      } else if (/Windows 10|Server 2016|Server 2019/.test(OS)) {
        outdated++;
      }
      total++;
    }
  } catch (error) { console.log('Error _Network_OS_status_helper', error); }
  const avg = total > 0 ? Math.round(100 * outdated / total) : 0;
  return [outdated, total, avg];
}

/**
 * Calculate Services finding for network devices
 */
export function calculateNetworkServicesFinding(devices, ACCEPTABLE, category_thresholds) {
  const [suspicious, total, avg] = _Network_Services_status_helper(devices);
  const status = getFindingStatus('Services (Network Scan)', avg, category_thresholds, ACCEPTABLE);
  const notes = suspicious > 0 
    ? `${suspicious} system${suspicious > 1 ? 's' : ''} have suspicious non-default services.` 
    : 'No suspicious non-default services found.';
  return ['Services (Network Scan)', status, avg, notes, 0, { count: suspicious, total: total, percent: avg }, 'Network Scan'];
}

/**
 * Helper for Services status
 */
function _Network_Services_status_helper(devices) {
  const device_suspicious = new Set();
  const total = devices.length || 0;
  try {
    for (const device of devices) {
      const ip = device['ip'] || '';
      const services = device?.system?.['Non-Default Services'] || device?.system?.['services'];
      if (!Array.isArray(services)) continue;
      for (const service of services) {
        const account = service['Start Account'] || service['account'];
        const state = service['State'] || service['state'];
        const startMode = service['Start Mode'] || service['start_mode'];
        if (["localSystem", "Administrator"].includes(account)) {
          if (state === 'Running' && startMode === 'Auto') {
            device_suspicious.add(ip);
          }
        }
      }
    }
  } catch (error) { console.log('Error _Network_Services_status_helper', error); }
  const suspicious = device_suspicious.size;
  const avg = total > 0 ? Math.round(100 * suspicious / total) : 0;
  return [suspicious, total, avg];
}

/**
 * Calculate Drive finding for network devices
 */
export function calculateNetworkDriveFinding(devices, ACCEPTABLE, category_thresholds) {
  const [above85, total, avg] = _Network_Drive_status_helper(devices);
  const status = getFindingStatus('Drive (Network Scan)', avg, category_thresholds, ACCEPTABLE);
  const notes = above85 > 0 
    ? `${above85} drive${above85 > 1 ? 's are' : ' is'} nearing critical usage levels (>85%).` 
    : 'All drives are in a safe state.';
  return ['Drive (Network Scan)', status, avg, notes, 0, { count: above85, total: total, percent: avg }];
}

/**
 * Helper for Drive status
 */
function _Network_Drive_status_helper(devices) {
  let above85 = 0;
  let total = 0;
  try {
    for (const device of devices) {
      const drives = device?.system?.['Logical Disks'] || device?.system?.['drives'];
      if (!Array.isArray(drives)) continue;
      const usage = getOverallDiskUsage(drives);
      if (usage > 85) above85++;
      total++;
    }
  } catch (error) { console.log('Error _Network_Drive_status_helper', error); }
  const avg = total > 0 ? Math.round(100 * above85 / total) : 0;
  return [above85, total, avg];
}

/**
 * Calculate Overall Disk Usage
 */
function getOverallDiskUsage(logicalDisks) {
  if (!Array.isArray(logicalDisks) || logicalDisks.length === 0) return 0;
  let totalSize = 0, totalUsed = 0;
  for (const disk of logicalDisks) {
    const size = parseFloat(disk["Size (GB)"] || disk["Size"] || disk.size || 0);
    const used = parseFloat(disk["Used (GB)"] || disk["Used"] || disk.used || 0);
    totalSize += size;
    totalUsed += used;
  }
  return totalSize === 0 ? 0 : (totalUsed / totalSize) * 100;
}

/**
 * Calculate Workstation Age finding for AD Scan
 */
export function calculateADWorkstationAgeFinding(ad, ACCEPTABLE, category_thresholds) {
  try {
    const computers = ad["AD Computers"] || ad.Computers || [];
    if (!Array.isArray(computers) || computers.length === 0) return null;

    const categoryName = 'Age';
    const cleanName = categoryName.trim();
    let thresholds = category_thresholds?.[cleanName];
    if (!thresholds) {
      const lowerName = cleanName.toLowerCase();
      const match = Object.entries(category_thresholds || {}).find(([k]) => k.trim().toLowerCase() === lowerName);
      if (match) thresholds = match[1];
    }

    let maxAgeMonths = 0;
    let oldCounts = 0;
    let totalProcessed = 0;
    const now = new Date();
    
    // Fallback boundaries if thresholds missing
    const warningLowerBound = thresholds?.rules?.Moderate?.lower || 25;
    const criticalLowerBound = thresholds?.rules?.Bad?.value || 36;
    
    let warningCount = 0;
    let criticalCount = 0;

    computers.forEach(computer => {
      const createdValue = computer.whenCreated || computer.created;
      if (!createdValue) return;

      try {
        let createdDate;
        if (typeof createdValue === 'string' && createdValue.includes('.0Z')) {
          const year = parseInt(createdValue.substring(0, 4));
          const month = parseInt(createdValue.substring(4, 6)) - 1;
          const day = parseInt(createdValue.substring(6, 8));
          const hour = parseInt(createdValue.substring(8, 10));
          const minute = parseInt(createdValue.substring(10, 12));
          const second = parseInt(createdValue.substring(12, 14));
          createdDate = new Date(Date.UTC(year, month, day, hour, minute, second));
        } else {
          createdDate = new Date(createdValue);
        }

        if (!isNaN(createdDate.getTime())) {
          const diffTime = Math.abs(now - createdDate);
          const ageMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30.4375));
          
          if (ageMonths > maxAgeMonths) maxAgeMonths = ageMonths;
          
          if (ageMonths >= warningLowerBound && ageMonths <= criticalLowerBound) warningCount++;
          if (ageMonths > criticalLowerBound) criticalCount++;
          if (ageMonths >= warningLowerBound) oldCounts++;

          totalProcessed++;
        }
      } catch (e) {
        // Silent fail for individual computer parsing
      }
    });

    // Propagation logic: Red > Yellow > Green
    let status = 'Healthy';
    if (criticalCount > 0) status = 'Critical';
    else if (warningCount > 0) status = 'Warning';

    // Compliance scorecard matches the % of healthy workstations
    const compliance = totalProcessed > 0 ? Math.round(((totalProcessed - (criticalCount + warningCount)) / totalProcessed) * 100) : 0;
    
    let countToDisplay = 0;
    let notes = '';
    
    if (status === 'Critical') {
        countToDisplay = criticalCount;
        notes = `${criticalCount} workstation${criticalCount > 1 ? 's' : ''} detected over ${criticalLowerBound} months old.`;
    } else if (status === 'Warning') {
        countToDisplay = warningCount;
        notes = `${warningCount} workstation${warningCount > 1 ? 's' : ''} detected between ${warningLowerBound} and ${criticalLowerBound} months old.`;
    } else {
        countToDisplay = 0;
        notes = `All workstations are within acceptable age ranges.`;
    }

    return [categoryName, status, compliance, notes, 0, { count: countToDisplay, total: totalProcessed, percent: compliance }, "Scan Attribution"];
  } catch (e) {
    console.error('Error calculating AD workstation age finding', e);
    return null;
  }
}

export function calculateNetworkAgeFinding(devices, ACCEPTABLE, category_thresholds) {
  try {
    const categoryName = 'Age'; // Legacy name matching DB for Network Scan
    const cleanName = categoryName.trim();
    let thresholds = category_thresholds?.[cleanName];
    if (!thresholds) {
      const lowerName = cleanName.toLowerCase();
      const match = Object.entries(category_thresholds || {}).find(([k]) => k.trim().toLowerCase() === lowerName);
      if (match) thresholds = match[1];
    }
  
    // Fallback boundaries if thresholds missing
    const warningLowerBound = thresholds?.rules?.Moderate?.lower || 25;
    const criticalLowerBound = thresholds?.rules?.Bad?.value || 36;
    
    const [maxAgeMonths, warningCount, criticalCount, totalProcessed] = _Network_Age_status_helper(devices, warningLowerBound, criticalLowerBound);
    
    // Propagation logic: Red > Yellow > Green
    let status = 'Healthy';
    if (criticalCount > 0) status = 'Critical';
    else if (warningCount > 0) status = 'Warning';

    // Compliance scorecard matches the % of healthy devices
    const compliance = totalProcessed > 0 ? Math.round(((totalProcessed - (criticalCount + warningCount)) / totalProcessed) * 100) : 0;
    
    let countToDisplay = 0;
    let notes = '';
    
    if (status === 'Critical') {
        countToDisplay = criticalCount;
        notes = `${criticalCount} device${criticalCount > 1 ? 's' : ''} detected over ${criticalLowerBound} months old.`;
    } else if (status === 'Warning') {
        countToDisplay = warningCount;
        notes = `${warningCount} device${warningCount > 1 ? 's' : ''} detected between ${warningLowerBound} and ${criticalLowerBound} months old.`;
    } else {
        countToDisplay = 0;
        notes = `All devices are within acceptable age ranges.`;
    }
  
    return [categoryName, status, compliance, notes, 0, { count: countToDisplay, total: totalProcessed, percent: compliance }, "Scan Attribution"];
  } catch (e) {
    console.error('Error calculating Network Age finding', e);
    return null;
  }
}

/**
 * Helper for Age status
 */
function _Network_Age_status_helper(devices, warningLowerBound, criticalLowerBound) {
  let maxAgeMonths = 0;
  let warningCount = 0;
  let criticalCount = 0;
  let total = 0;
  try {
    for (const device of devices) {
      // Network devices typically report age in years.
      const ageYears = device?.system?.['Age'] || device?.system?.age;
      if (typeof ageYears !== 'number' || isNaN(ageYears)) continue;
      
      const ageMonths = Math.floor(ageYears * 12);
      if (ageMonths > maxAgeMonths) maxAgeMonths = ageMonths;
      
      if (ageMonths >= warningLowerBound && ageMonths <= criticalLowerBound) warningCount++;
      if (ageMonths > criticalLowerBound) criticalCount++;
      total++;
    }
  } catch (error) { console.log('Error _Network_Age_status_helper', error); }
  return [maxAgeMonths, warningCount, criticalCount, total];
}

/**
 * Calculate Softwares finding for network devices
 */
export function calculateNetworkSoftwaresFinding(devices, ACCEPTABLE, category_thresholds) {
  const [outdated, total, avg] = _Network_Softwares_status_helper(devices);
  const status = getFindingStatus('Softwares (Network Scan)', avg, category_thresholds, ACCEPTABLE);
  const notes = outdated > 0 
    ? `${outdated} common software installation${outdated > 1 ? 's are' : ' is'} outdated.` 
    : 'All common software is up to date.';
  return ['Softwares (Network Scan)', status, avg, notes, 0, { count: outdated, total: total, percent: avg }, 'Network Scan'];
}

/**
 * Helper for Softwares status
 */
function _Network_Softwares_status_helper(devices) {
  let outdated = 0;
  let total = 0;
  try {
    for (const device of devices) {
      const CS = device?.system?.['Common_softwares'] || device?.system?.['software'];
      if (!Array.isArray(CS)) continue;
      for (const app of CS) {
        if (app[3] !== 'Up to date' && app.status !== 'Up to date') {
          outdated++;
        }
        total++;
      }
    }
  } catch (error) { console.log('Error _Network_Softwares_status_helper', error); }
  const avg = total > 0 ? Math.round(100 * outdated / total) : 0;
  return [outdated, total, avg];
}

/**
 * Calculate Backups summary from raw device data
 * Matches _Network_Backups_status and Network_Backups_status logic from template2/Network.js
 * Returns: { category, status, statusColor, compliance, notes } or null
 */


/**
 * Extract open ports data with risk categorization
 */
// Export for use in ReportBuilder pagination calculation
export function extractOpenPortsData(data) {
  const portsData = {
    sensitive: [],
    high: [],
    medium: [],
    low: [],
    all: []
  };

  if (!data) return portsData;

  // Sensitive ports mapping - EXACT MATCH from template2_doc/Network.js line 482
  // This maps port numbers to their service names
  const sensitivePorts = {
    21: "FTP",
    22: "SSH",
    23: "Telnet",
    25: "SMTP",
    53: "DNS",
    80: "HTTP",
    139: "NetBIOS",
    143: "SMB",
    3306: "MySQL",
    3389: "RDP",
    8080: "HTTP"
  };

  // Extract from raw scan data
  // Handle wrapped data structure (scanData, data, or direct)
  // Priority: scanData > data > direct access
  const scanDataSource = data?.scanData || data?.data || data;

  console.log('[OpenPortsWidget] Data structure:', {
    hasScanData: !!data?.scanData,
    hasData: !!data?.data,
    hasDirect: !!data?.network_devices,
    scanDataSourceKeys: scanDataSource ? Object.keys(scanDataSource).slice(0, 10) : [],
    hasNetworkDevices: !!scanDataSource?.network_devices
  });

  // Ensure we only process network_devices once, even if it exists in multiple places
  // Use a Set to track processed devices by IP to prevent duplicates
  const processedDevices = new Set();

  if (scanDataSource && scanDataSource.network_devices) {
    const networkDevices = Array.isArray(scanDataSource.network_devices)
      ? scanDataSource.network_devices
      : [];

    console.log('[OpenPortsWidget] Found network_devices:', networkDevices.length);

    networkDevices.forEach(device => {
      if (!device.open_ports) return;

      const ip = device.ip || 'Unknown';
      const deviceKey = `${ip}_${device.hostname || ''}`;

      // Skip if we've already processed this device (prevent duplicates)
      if (processedDevices.has(deviceKey)) {
        console.warn('[OpenPortsWidget] Skipping duplicate device:', deviceKey);
        return;
      }
      processedDevices.add(deviceKey);

      // Only extract SENSITIVE ports (matching template logic)
      Object.keys(device.open_ports).forEach(portKey => {
        const portNum = parseInt(portKey);

        // Only include if it's a sensitive port
        if (sensitivePorts[portNum]) {
          const portInfo = {
            port: portNum,
            service: sensitivePorts[portNum], // Use mapped service name, NOT data value
            ip: ip,
            device: device.hostname || ip
          };

          portsData.all.push(portInfo);
        }
      });
    });
  }

  console.log('[OpenPortsWidget] Extracted SENSITIVE ports:', portsData.all.length);
  return portsData;
}

/**
 * Extract all findings with details
 */
function extractAllFindings(data, filterCategory = null) {
  const findings = [];

  if (!data) return findings;

  const exportDataSource = data.exportData || data;

  if (!exportDataSource || typeof exportDataSource !== 'object') return findings;

  Object.entries(exportDataSource).forEach(([category, categoryData]) => {
    if (filterCategory && category !== filterCategory) return;
    if (!Array.isArray(categoryData) || categoryData.length < 2) return;

    const rectangles = categoryData[0];

    if (!Array.isArray(rectangles)) return;

    rectangles.forEach(finding => {
      findings.push({
        title: finding.outerText || 'Unknown',
        items: finding.innerListItems || [],
        color: finding.color || 'gray',
        score: finding.score,
        category
      });
    });
  });

  console.log('[FindingsGridWidget] Extracted findings:', findings.length);
  return findings;
}

/**
 * Check backups from raw device data
 */


/**
 * Extract raw open ports from devices
 */


/**
 * Extract Open Ports Summary (Total Devices, Affected Devices, Percentage)
 */
function extractOpenPortsSummary(data) {
  const coverDataSource = data.coverData || data.keyValue || {};

  // Check Network Scan and Localhost Scan for open ports data
  let totalDevices = 0;
  let affectedDevices = 0;

  // Network Scan
  if (coverDataSource['Network Scan']) {
    const networkData = coverDataSource['Network Scan'];
    totalDevices += networkData.total_devices || 0;
    affectedDevices += networkData.affected_devices || 0;
  }

  // Localhost Scan
  if (coverDataSource['Localhost Scan']) {
    const localhostData = coverDataSource['Localhost Scan'];
    totalDevices += localhostData.total_devices || 0;
    affectedDevices += localhostData.affected_devices || 0;
  }

  const percentage = totalDevices > 0 ? Math.round((affectedDevices / totalDevices) * 100) : 0;

  return { totalDevices, affectedDevices, percentage };
}

/**
 * Extract Dark Web Summary (Total Emails, Breached Emails, Percentage)
 */
function extractDarkWebSummary(data) {
  const coverDataSource = data.coverData || data.keyValue || {};

  let totalEmails = 0;
  let breachedEmails = 0;

  // Check for dark web / breach data
  if (coverDataSource['Personally Identifiable Information']) {
    const piiData = coverDataSource['Personally Identifiable Information'];
    totalEmails = piiData.total_email || 0;
    breachedEmails = piiData.breached_email || piiData.darkweb_email || 0;
  }

  const percentage = totalEmails > 0 ? Math.round((breachedEmails / totalEmails) * 100) : 0;

  return { totalEmails, breachedEmails, percentage };
}

/**
 * Extract Accounts Summary (Total Accounts, Non-Expiring Passwords, Percentage)
 */
function extractAccountsSummary(data) {
  const coverDataSource = data.coverData || data.keyValue || {};

  let totalAccounts = 0;
  let nonExpiring = 0;

  // Check Active Directory Scan for account data
  if (coverDataSource['Active Directory Scan']) {
    const adData = coverDataSource['Active Directory Scan'];
    totalAccounts = adData.total_accounts || adData.AD_Users || adData['AD Users'] || 0;
    nonExpiring = adData.non_expiring_password || adData.password_never_expires || 0;
  }

  const percentage = totalAccounts > 0 ? Math.round((nonExpiring / totalAccounts) * 100) : 0;

  return { totalAccounts, nonExpiring, percentage };
}

/**
 * Extract Admins Summary (Total Admins, breakdown by category)
 */
function extractAdminsSummary(data) {
  const coverDataSource = data.coverData || data.keyValue || {};

  let totalAdmins = 0;
  const admins = [];

  // Check Active Directory Scan for admin data
  if (coverDataSource['Active Directory Scan']) {
    const adData = coverDataSource['Active Directory Scan'];

    // Total admins
    totalAdmins = adData.total_admin || adData.total_admins || 0;

    // Breakdown by category
    const domainAdmins = adData.domain_admin || adData.domain_admins || 0;
    const localAdmins = adData.local_admin || adData.local_admins || 0;
    const enterpriseAdmins = adData.enterprise_admin || adData.enterprise_admins || 0;

    let cumulative = 0;

    if (domainAdmins > 0) {
      cumulative += domainAdmins;
      admins.push({ category: 'Domain Admins', total: domainAdmins, cumulative });
    }

    if (localAdmins > 0) {
      cumulative += localAdmins;
      admins.push({ category: 'Local Admins', total: localAdmins, cumulative });
    }

    if (enterpriseAdmins > 0) {
      cumulative += enterpriseAdmins;
      admins.push({ category: 'Enterprise Admins', total: enterpriseAdmins, cumulative });
    }
  }

  return { totalAdmins, admins };
}


/**
 * Extract Service Risk Summary (Total Devices, Affected Devices, Percentage)
 */
function extractServiceRiskSummary(data) {
  const coverDataSource = data.coverData || data.keyValue || {};

  let totalDevices = 0;
  let affectedDevices = 0;

  // Check for risky services across scans
  ['Network Scan', 'Localhost Scan'].forEach(scanType => {
    if (coverDataSource[scanType]) {
      const scanData = coverDataSource[scanType];
      totalDevices += scanData.total_devices || 0;
      affectedDevices += scanData.risky_services_devices || scanData.affected_by_services || 0;
    }
  });

  const percentage = totalDevices > 0 ? Math.round((affectedDevices / totalDevices) * 100) : 0;

  return { totalDevices, affectedDevices, percentage };
}

/**
 * Extract Overall Risk Score data
 */
function extractOverallRiskScore(data) {
  // Use Key Findings Summary data as the source
  const summaries = extractKeyFindingsSummary(data);

  // DEBUG: Log widget data
  console.log('===== OVERALL RISK SCORE DEBUG =====');
  console.log('Key Findings Summary Categories:', summaries.length);
  console.log('Summaries:', summaries);

  // Count categories based on compliance percentages from Key Findings Summary
  // 51% to 100% compliance = High Risks
  // 21% to 50% compliance = Low Risks
  // 0% to 20% compliance = Good
  let goodCount = 0;
  let lowRiskCount = 0;
  let highRiskCount = 0;

  summaries.forEach(summary => {
    if (summary.statusColor === 'red') {
      highRiskCount++;
    } else if (summary.statusColor === 'yellow') {
      lowRiskCount++;
    } else if (summary.statusColor === 'green') {
      goodCount++;
    }
  });

  // Calculate overall score (average compliance)
  let totalCompliance = 0;
  summaries.forEach(summary => {
    totalCompliance += summary.compliance || 0;
  });
  const overallScore = summaries.length > 0 ? Math.round(totalCompliance / summaries.length) : 0;
  const ACCEPTABLE = summaries.ACCEPTABLE || 20;

  let riskLevel = 'good';
  let riskLabel = 'Acceptable';

  if (overallScore > (ACCEPTABLE * 2.5)) {
    riskLevel = 'high';
    riskLabel = 'Critical Risk';
  } else if (overallScore > ACCEPTABLE) {
    riskLevel = 'low';
    riskLabel = 'At Risk';
  }

  // Calculate status message using category counts
  let statusMessage = 'LOW RISK';
  let statusColor = 'good';

  if (highRiskCount >= 3 || (highRiskCount + lowRiskCount) > 5) {
    statusMessage = 'HIGH RISK';
    statusColor = 'high';
  } else if (highRiskCount === 1 || highRiskCount === 2 || lowRiskCount >= 3) {
    statusMessage = 'ELEVATED RISK';
    statusColor = 'low';
  }

  // Total Issues = Low Risk + High Risk (sum of categories)
  const totalIssues = lowRiskCount + highRiskCount;

  return {
    overallScore,
    riskLevel,
    riskLabel,
    goodCount,
    lowRiskCount,
    highRiskCount,
    statusMessage,
    statusColor,
    totalIssues
  };
}

